import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  CircularProgress,
  Tooltip,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Menu,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import {
  Close as CloseIcon,
  Chat as ChatIcon,
  MoreVert as MoreVertIcon,
  AccessTime as AccessTimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Notes as NotesIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import { ApiEndPoints, IMAGE_URL, MEDIA_URL } from "src/network/endpoints";
import { axiosInstance } from "src/network/adapter";
import { toast } from "react-hot-toast";
import CustomizeIcon from "src/@core/components/icons/CustomizeIcon";
import { useQuery } from "react-query";
import BusinessCancelSelectionDialog from "./BusinessCancelSelectionDialog";
import BusinessCancelDialog from "./BusinessCancelDialog";
import BusinessRefundSelectionDialog from "./BusinessRefundSelectionDialog";
import BusinessRefundDialog from "./BusinessRefundDialog";
import BusinessRefundDetailsDialog from "./BusinessRefundDetailsDialog";
import currencyData from "src/constants/currencyData.json";

// Styled Components - Exactly from OrderDetailsDialog
const CustomOverlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10000,
  pointerEvents: "none",
});

const CustomDialog = styled(Box)(({ theme }) => ({
  borderRadius: 20,
  maxWidth: "95vw",
  maxHeight: "95vh",
  width: "900px",
  height: "700px",
  position: "fixed",
  overflow: "hidden",
  background: "white",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  pointerEvents: "auto",
  [theme.breakpoints.down("md")]: { width: "90vw", height: "80vh" },
  [theme.breakpoints.down("sm")]: {
    width: "95vw",
    height: "80vh",
    borderRadius: 16,
  },
  [theme.breakpoints.down(400)]: {
    width: "90vw",
    height: "80vh",
    borderRadius: 12,
  },
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  width: 25,
  height: 25,
  cursor: "se-resize",
  background:
    "linear-gradient(135deg, rgba(224,224,224,0.9) 0%, rgba(189,189,189,0.9) 100%)",
  borderTopLeftRadius: 20,
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 2,
    right: 2,
    width: 15,
    height: 15,
    background: `repeating-linear-gradient(
      45deg, 
      rgba(158,158,158,0.8), 
      rgba(158,158,158,0.8) 2px, 
      transparent 2px, 
      transparent 4px
    )`,
  },
  [theme.breakpoints.down("sm")]: {
    width: 20,
    height: 20,
    borderTopLeftRadius: 16,
    "&::before": {
      bottom: 1,
      right: 1,
      width: 12,
      height: 12,
    },
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 24px",
  background: "white",
  position: "relative",
  zIndex: 10,
  cursor: "move",
  userSelect: "none",
  [theme.breakpoints.down("sm")]: { padding: "12px 16px" },
  [theme.breakpoints.down(400)]: { padding: "10px 12px" },
}));

const DragHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "50%",
  top: 8,
  transform: "translateX(-50%)",
  width: 40,
  height: 4,
  backgroundColor: "#E2E8F0",
  borderRadius: 2,
  cursor: "move",
  "&:hover": { backgroundColor: "#CBD5E1" },
  [theme.breakpoints.down("sm")]: { width: 32, height: 3, top: 6 },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  background: "white",
  borderRadius: 16,
  height: "calc(100% - 100px)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: "0 24px",
  [theme.breakpoints.down("sm")]: {
    padding: "0 16px",
    height: "calc(100% - 80px)",
    borderRadius: 12,
  },
  [theme.breakpoints.down(400)]: {
    padding: "0 12px",
    height: "calc(100% - 70px)",
    borderRadius: 8,
  },
}));

const ScrollableContainer = styled(Box)({
  flex: 1,
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "#F1F5F9", borderRadius: 10 },
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1",
    borderRadius: 10,
    "&:hover": { background: "#94A3B8" },
  },
});

// Business Info Header
const BusinessHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "end",
  justifyContent: "space-between",
  padding: "16px 24px",
  backgroundColor: "#f8f9fa",
  borderBottom: "1px solid #e5e7eb",
  [theme.breakpoints.down("sm")]: { padding: "8px" },
}));

const BusinessInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const BusinessLogo = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  backgroundColor: "#FFD700",
  color: "#000000",
  fontWeight: "bold",
  fontSize: "1.2rem",
  [theme.breakpoints.down("sm")]: {
    width: 50,
    height: 50,
    fontSize: "0.9rem",
  },
}));

// User Card with Purple Background
const UserCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#f0f4ff",
  border: "1px solid #d1d5db",
  borderRadius: theme.spacing(2),
  margin: theme.spacing(2, 0),
  padding: "8px 24px",
  [theme.breakpoints.down("sm")]: { padding: "8px 16px" },
  [theme.breakpoints.down(400)]: { padding: "4px 12px" },
}));

const UserInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "end",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));

// Tabs
const CustomTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTab-root": {
    textTransform: "lowercase",
    fontWeight: 600,
    fontSize: "1rem",
    minHeight: 46,
    padding: "8px 16px",
    borderRadius: "12px 12px 0 0",
    marginRight: 4,
    color: "black",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&.Mui-selected": {
      color: "#7C3AED",
      backgroundColor: "#f0f4ff",
      fontWeight: 700,
    },
    "&:hover": { backgroundColor: "#f0f4ff", color: "#7C3AED" },
  },
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTabs-flexContainer": { borderBottom: "2px solid #E2E8F0" },
}));

// Colored Table for Order Items
const ColoredTable = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  border: "1px solid #e5e7eb",
  borderRadius: theme.spacing(1),
  // Removed nth-of-type background colors - now using status-based colors from itemStatus
  "& .MuiTableCell-root": {
    padding: "4px !important",
  },
  "& .MuiTableCell-head": {
    padding: "4px !important",
  },
  "& .MuiTableCell-body": {
    padding: "4px !important",
  },
  "& table .MuiTableCell-root": {
    padding: "4px !important",
  },
  "& .MuiTable-root .MuiTableCell-root": {
    padding: "4px !important",
  },
}));

/**
 * BusinessOrderDetailsDialog Component
 * 
 * Displays order details with customer cancellations shown with strikethrough
 * Uses EXACT same UI as OrderDetailsDialog
 */
const BusinessOrderDetailsDialog = ({ 
  open, 
  onClose, 
  orderId,
  messageType, // Type of message that opened this dialog (e.g., "order_cancellation_request")
  conversationInfo, // Conversation info with admin and users arrays for group members
  currentUser,  // Current user object (to detect if owner is also a customer participant)
  storedUserId, // Fallback user ID from storage
  onPayAsCustomer // Callback to open customer payment dialog when owner has pending split payment
}) => {
  const dialogRef = useRef(null);
  const autoActivatedRef = useRef(false); // Track if delete icon has been auto-activated
  const selectRef = useRef(null);

  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [expandedSides, setExpandedSides] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  
  // State for business cancel detail dialog
  const [businessCancelDetailDialogOpen, setBusinessCancelDetailDialogOpen] = useState(false);
  const [businessCancelDetailDialogData, setBusinessCancelDetailDialogData] = useState(null);
  const [tempBusinessSelection, setTempBusinessSelection] = useState(new Set());
  
  // State for reject confirmation dialog
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  
  // State for business cancel dialog (new)
  const [businessCancelDialogOpen, setBusinessCancelDialogOpen] = useState(false);
  const [businessCancelDialogData, setBusinessCancelDialogData] = useState(null);
  
  // State for refund flow
  const [refundMode, setRefundMode] = useState(false); // Whether we're in refund mode
  const [refundSelectedItems, setRefundSelectedItems] = useState(new Set());
  const [businessRefundSelectionDialogOpen, setBusinessRefundSelectionDialogOpen] = useState(false);
  const [businessRefundSelectionDialogData, setBusinessRefundSelectionDialogData] = useState(null);
  const [tempRefundSelection, setTempRefundSelection] = useState(new Set());
  const [businessRefundDialogOpen, setBusinessRefundDialogOpen] = useState(false);
  const [businessRefundDialogData, setBusinessRefundDialogData] = useState(null);
  const [refundDetailsOpen, setRefundDetailsOpen] = useState(false);
  
  // State for selected user in dropdown
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  // State to store all original group members (to show in dropdown even after reassignment)
  const [allOriginalGroupMembers, setAllOriginalGroupMembers] = useState([]);
  
  // State for edit mode
  const [editMode, setEditMode] = useState(false);
  
  // State for options menu
  const [optionsMenuAnchor, setOptionsMenuAnchor] = useState(null);
  
  // State for table number
  const [tableNumber, setTableNumber] = useState("");
  
  // State for bulk item selection in edit mode
  const [selectedItemsForBulkAssignment, setSelectedItemsForBulkAssignment] = useState(new Set());

  // Dialog positioning and resizing
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ x: 100, y: 100 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // React Query for fetching order data
  const { data: orderData, isLoading: loading, error, refetch } = useQuery(
    ['cancellation-order-details', orderId],
    async () => {
      const response = await axiosInstance.get(
        ApiEndPoints.ORDER.getOrderById(orderId)
      );

      if (response.data?.status === "success" && response.data?.data?.order) {
        return response.data.data.order;
      } else {
        throw new Error("Invalid response format");
      }
    },
    {
      enabled: open && !!orderId,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      onError: (error) => {
        console.error("Error fetching order details:", error);
        toast.error("failed to load order details");
      }
    }
  );

  // Drag and resize functionality (from MenuDialog)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const maxX = window.innerWidth - 400;
        const maxY = window.innerHeight - 300;
        setDialogPosition({
          x: Math.max(0, Math.min(e.clientX - dragOffset.x, maxX)),
          y: Math.max(0, Math.min(e.clientY - dragOffset.y, maxY)),
        });
      } else if (isResizing && dialogRef.current) {
        const rect = dialogRef.current.getBoundingClientRect();
        const newWidth = e.clientX - rect.left;
        const newHeight = e.clientY - rect.top;

        if (newWidth > 400 && newWidth < window.innerWidth * 0.95) {
          dialogRef.current.style.width = `${newWidth}px`;
        }
        if (newHeight > 300 && newHeight < window.innerHeight * 0.95) {
          dialogRef.current.style.height = `${newHeight}px`;
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset]);

  // Initialize dialog position when opened
  useEffect(() => {
    if (open) {
      const isMobile = window.innerWidth < 960;
      const dialogWidth = isMobile ? window.innerWidth * 0.9 : 900;
      const dialogHeight = isMobile ? window.innerHeight * 0.8 : 700;
      const centerX = (window.innerWidth - dialogWidth) / 2;
      const centerY = (window.innerHeight - dialogHeight) / 2;

      setDialogPosition({
        x: Math.max(0, centerX),
        y: Math.max(0, centerY),
      });
      
      // Reset auto-activation flag when dialog opens
      autoActivatedRef.current = false;
    }
  }, [open]);

  // Set default selected user to order initiator when orderData is available
  useEffect(() => {
    if (orderData?.userId && !selectedUserId) {
      const aiUserId = process.env.REACT_APP_AI_USER_ID;
      const initiatorId = orderData.userId._id || orderData.userId;
      const normalizedInitiatorId = initiatorId ? String(initiatorId) : null;
      
      // If initiator is not beemz ai, use it
      if (normalizedInitiatorId && normalizedInitiatorId !== aiUserId) {
        setSelectedUserId(normalizedInitiatorId);
      } else {
        // If initiator is beemz ai, select the first available group member from userOrders
        let firstAvailableMember = null;
        
        if (orderData?.groupOrder?.userOrders) {
          const seenUserIds = new Set();
          for (const userOrder of orderData.groupOrder.userOrders) {
            const userId = userOrder.userId?._id || userOrder.userId;
            const normalizedUserId = userId ? String(userId) : null;
            if (normalizedUserId && normalizedUserId !== aiUserId && !seenUserIds.has(normalizedUserId)) {
              seenUserIds.add(normalizedUserId);
              firstAvailableMember = normalizedUserId;
              break;
            }
          }
        }
        
        if (firstAvailableMember) {
          setSelectedUserId(firstAvailableMember);
        }
      }
    }
  }, [orderData, selectedUserId]);

  // Store all original group members when order data is loaded
  useEffect(() => {
    if (orderData?.groupOrder?.userOrders) {
      const aiUserId = process.env.REACT_APP_AI_USER_ID;
      const uniqueUsers = [];
      const seenUserIds = new Set();

      orderData.groupOrder.userOrders.forEach((userOrder) => {
        const userId = userOrder.userId?._id || userOrder.userId;
        const normalizedUserId = userId ? String(userId) : null;
        
        // Skip if already seen, invalid, or is beemz ai
        if (!normalizedUserId || seenUserIds.has(normalizedUserId) || normalizedUserId === aiUserId) {
          return;
        }

        seenUserIds.add(normalizedUserId);

        uniqueUsers.push({
          userId: normalizedUserId,
          fullName: userOrder.userFullName || userOrder.userId?.fullName || userOrder.userName || 'Unknown User',
          userName: userOrder.userName || userOrder.userId?.userName || '',
          profilePic: userOrder.userProfilePic || userOrder.userId?.image?.profilePic || '',
          amountPaid: userOrder.amountPaid || 0,
          subtotal: userOrder.subtotal || 0,
        });
      });

      // Only update if we have multiple userOrders (it's a group order)
      // Store all unique users so dropdown always shows all options even after reassignment
      if (orderData.groupOrder.userOrders.length > 1) {
        setAllOriginalGroupMembers(uniqueUsers);
      } else {
        setAllOriginalGroupMembers([]);
      }
    } else {
      setAllOriginalGroupMembers([]);
    }
  }, [orderData]);

  // Initialize table number from orderData
  useEffect(() => {
    if (orderData) {
      if (orderData.tableNumber !== undefined && orderData.tableNumber !== null) {
        setTableNumber(String(orderData.tableNumber));
      } else {
        setTableNumber("");
      }
    }
  }, [orderData]);

  // Fix z-index for Select dropdown menu
  useEffect(() => {
    if (!open) return;

    const fixSelectZIndex = () => {
      // Find all Popover elements and set z-index
      const popovers = document.querySelectorAll('.MuiPopover-root, .group-members-popover, [role="presentation"]');
      popovers.forEach((popover) => {
        if (popover instanceof HTMLElement) {
          const computedStyle = window.getComputedStyle(popover);
          // Only update if it's a popover (has position fixed or absolute)
          if (computedStyle.position === 'fixed' || computedStyle.position === 'absolute') {
            popover.style.zIndex = '20000';
            // Also update any child elements that might need z-index
            const paper = popover.querySelector('.MuiPaper-root');
            if (paper instanceof HTMLElement) {
              paper.style.zIndex = '20000';
            }
          }
        }
      });
    };

    // Run immediately
    fixSelectZIndex();
    
    // Set up interval to check periodically (for when menu opens)
    const interval = setInterval(fixSelectZIndex, 100);
    
    // Also set up a mutation observer
    const observer = new MutationObserver(() => {
      setTimeout(fixSelectZIndex, 0);
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [open]);

  // Auto-activate delete icon when messageType is "order_cancellation_request"
  // Only activate ONCE when dialog first opens, not after refetch
  useEffect(() => {
    if (open && orderData && messageType === "order_cancellation_request" && !autoActivatedRef.current) {
        handleDeleteIconClick(); // Reuse existing function
        autoActivatedRef.current = true; // Mark as activated
    }
  }, [open, orderData, messageType]);

  // Event handlers
  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    if (dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle options menu
  const handleOptionsMenuOpen = (event) => {
    setOptionsMenuAnchor(event.currentTarget);
  };

  const handleOptionsMenuClose = () => {
    setOptionsMenuAnchor(null);
  };

  const handleEditClick = async () => {
    // If turning OFF edit mode (was true, now false), save the table number
    if (editMode) {
      const conversationId = orderData?.conversationId;
      if (conversationId) {
        try {
          const response = await axiosInstance.put(
            ApiEndPoints.ORDER.updateTableNumber(conversationId),
            { tableNumber: tableNumber || "" }
          );
          
          if (response.data?.status === "success") {
            toast.success("table number updated  ");
            // Refetch order data to get updated information
            await refetch();
          } else {
            toast.error(response.data?.message || "failed to update table number");
          }
        } catch (error) {
          console.error("Error updating table number:", error);
          toast.error(error.response?.data?.message || "failed to update table number");
        }
      }
    }
    
    // Clear bulk selection when turning off edit mode
    if (editMode) {
      setSelectedItemsForBulkAssignment(new Set());
    }
    
    setEditMode(!editMode);
    handleOptionsMenuClose();
  };

  // Handle search input
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Handle delete icon click - toggle checkboxes and reset/initialize selection
  const handleDeleteIconClick = () => {
    if (refundMode) {
      // In refund mode - toggle refund selection
      if (showCheckboxes) {
        setShowCheckboxes(false);
        setRefundSelectedItems(new Set());
        setRefundMode(false);
      } else {
        setShowCheckboxes(true);
        const paidItems = getPaidItems();
        const initialSelected = new Set(paidItems.map(p => p.itemKey));
        setRefundSelectedItems(initialSelected);
      }
      return;
    }
    
    // Cancellation mode
    if (showCheckboxes) {
      // If checkboxes are already showing, reset everything back to initial state
      setShowCheckboxes(false);
      setSelectedItems(new Set());
    } else {
      // If checkboxes are not showing, enable them and pre-select cancelled items
      setShowCheckboxes(true);
      
      // Pre-select items with cancellation.requested: true (pending_approval and approved ONLY)
      // EXCLUDE items with status: "rejected" - treat them as normal items
      if (!orderData) return;
      
      const initialSelectedItems = new Set();
      const allOrderItems = orderData?.groupOrder?.userOrders || [];
      
      allOrderItems.forEach((userOrder, userIndex) => {
        const userCart = userOrder.cart || [];
        userCart.forEach((item, itemIndex) => {
          const menuItemId = item.menuItemId?._id;
          if (!menuItemId) return;
          
          if (item.variantEnabled && item.variants) {
            // For variant items - check each variant
            item.variants.forEach((variant, variantIndex) => {
              variant.items?.forEach(variantItem => {
                // Only include pending_approval and approved (exclude rejected)
                if (variantItem.cancellation?.requested && variantItem.cancellation.status !== "rejected") {
                  const itemKey = `${menuItemId}-${variant.variantName}-${variantItem._id}`;
                  initialSelectedItems.add(itemKey);
                }
              });
            });
          } else {
            // For non-variant items
            item.items?.forEach(subItem => {
              // Only include pending_approval and approved (exclude rejected)
              if (subItem.cancellation?.requested && subItem.cancellation.status !== "rejected") {
                const itemKey = `${menuItemId}-${subItem._id}`;
                initialSelectedItems.add(itemKey);
              }
            });
          }
        });
      });
      
      setSelectedItems(initialSelectedItems);
    }
  };

  // Helper function to check if an item is approved (cannot be modified)
  const isItemApproved = (item, variantName, itemId) => {
    if (!item || !orderData) return false;
    
    if (item.variantEnabled && variantName && item.variants) {
      const variant = item.variants.find(v => v.variantName === variantName);
      if (variant && variant.items) {
        const variantItem = variant.items.find(vi => vi._id === itemId);
        return variantItem?.cancellation?.status === "approved";
      }
    } else if (!item.variantEnabled && item.items) {
      const subItem = item.items.find(si => si._id === itemId);
      return subItem?.cancellation?.status === "approved";
    }
    
    return false;
  };

  // Handle item selection (only for pending_approval items)
  const handleItemSelect = (itemKey, item, variantName, itemId) => {
    // Check if item is approved - if so, don't allow selection changes
    if (isItemApproved(item, variantName, itemId)) {
      return;
    }
    
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(itemKey)) {
      newSelectedItems.delete(itemKey);
    } else {
      newSelectedItems.add(itemKey);
    }
    setSelectedItems(newSelectedItems);
  };

  // Handle batch item selection (for remaining quantity rows)
  const handleBatchItemSelect = (itemKeys, itemKeysData = []) => {
    const newSelectedItems = new Set(selectedItems);
    
    // Filter out approved items from batch selection
    const selectableKeys = itemKeys.filter((itemKey, index) => {
      if (itemKeysData[index]) {
        const { item, variantName, itemId } = itemKeysData[index];
        return !isItemApproved(item, variantName, itemId);
      }
      return true; // If no data provided, assume selectable
    });
    
    selectableKeys.forEach(itemKey => {
      if (newSelectedItems.has(itemKey)) {
        newSelectedItems.delete(itemKey);
      } else {
        newSelectedItems.add(itemKey);
      }
    });
    
    setSelectedItems(newSelectedItems);
  };

  // Handle minus icon click - open business cancel detail dialog to manage individual items
  const handleBusinessCancelClick = (itemData) => {
    // Get approved item ids from the item data
    const approvedItemIds = [];
    
    if (itemData.isVariant && itemData.variant) {
      itemData.variant.items?.forEach(variantItem => {
        if (variantItem.cancellation?.status === "approved") {
          approvedItemIds.push(variantItem._id);
        }
      });
    } else if (!itemData.isVariant && itemData.item) {
      itemData.item.items?.forEach(subItem => {
        if (subItem.cancellation?.status === "approved") {
          approvedItemIds.push(subItem._id);
        }
      });
    }
    
    setBusinessCancelDetailDialogData({
      ...itemData,
      approvedItemIds: approvedItemIds
    });
    
    // Initialize temp selection with currently selected items for this item/variant
    // EXCLUDE approved items from temp selection
    const tempSelection = new Set();
    itemData.allItemIds.forEach(itemId => {
      const itemKey = itemData.isVariant 
        ? `${itemData.menuItemId}-${itemData.variantName}-${itemId}`
        : `${itemData.menuItemId}-${itemId}`;
      
      // Only add to temp selection if it's selected AND not approved
      if (selectedItems.has(itemKey) && !approvedItemIds.includes(itemId)) {
        tempSelection.add(itemKey);
      }
    });
    
    setTempBusinessSelection(tempSelection);
    setBusinessCancelDetailDialogOpen(true);
  };

  // Handle "Add to cancellation" in business cancel dialog
  const handleBusinessCancelSubmit = () => {
    // Update main selectedItems with temp selection
    const newSelectedItems = new Set(selectedItems);
    
    // First, remove all items for this item/variant from main selection
    businessCancelDetailDialogData.allItemIds.forEach(itemId => {
      const itemKey = businessCancelDetailDialogData.isVariant 
      ? `${businessCancelDetailDialogData.menuItemId}-${businessCancelDetailDialogData.variantName}-${itemId}`
      : `${businessCancelDetailDialogData.menuItemId}-${itemId}`;
      newSelectedItems.delete(itemKey);
    });
    
    // Then add items from temp selection
    tempBusinessSelection.forEach(itemKey => {
      newSelectedItems.add(itemKey);
    });
    
    setSelectedItems(newSelectedItems);
    
    // Close dialog
    setBusinessCancelDetailDialogOpen(false);
    setBusinessCancelDetailDialogData(null);
    setTempBusinessSelection(new Set());
  };

  // Get all selectable item keys (ALL items in the order - both cancelled and remaining)
  const getAllSelectableKeys = () => {
    if (!orderData) return [];
    
    const allSelectableKeys = [];
    const allOrderItems = orderData?.groupOrder?.userOrders || [];
    
    allOrderItems.forEach((userOrder) => {
      const userCart = userOrder.cart || [];
      userCart.forEach((item) => {
        const menuItemId = item.menuItemId?._id;
        if (!menuItemId) return;
        
        if (item.variantEnabled && item.variants) {
          // For variant items - add each variant's item ids
          item.variants.forEach((variant) => {
            variant.items?.forEach(variantItem => {
              const itemKey = `${menuItemId}-${variant.variantName}-${variantItem._id}`;
              allSelectableKeys.push(itemKey);
            });
          });
        } else {
          // For non-variant items - add each item id
          item.items?.forEach(subItem => {
            const itemKey = `${menuItemId}-${subItem._id}`;
            allSelectableKeys.push(itemKey);
          });
        }
      });
    });
    
    return allSelectableKeys;
  };

  // Handle Select All / Deselect All in table
  const handleSelectAllInTable = () => {
    const allSelectableKeys = getAllSelectableKeys();
    if (allSelectableKeys.length === 0) return;
    
    // Check if all are currently selected
    const allSelected = allSelectableKeys.every(key => selectedItems.has(key));
    
    const newSelectedItems = new Set();
    
    if (!allSelected) {
      // Select all
      allSelectableKeys.forEach(key => newSelectedItems.add(key));
    }
    
    setSelectedItems(newSelectedItems);
  };

  // Get select all checkbox state
  const getSelectAllState = () => {
    const allSelectableKeys = getAllSelectableKeys();
    if (allSelectableKeys.length === 0) return { checked: false, indeterminate: false };
    
    const selectedCount = allSelectableKeys.filter(key => selectedItems.has(key)).length;
    
    // Only show checked when ALL items are selected, otherwise show unchecked (no indeterminate)
    if (selectedCount === allSelectableKeys.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: false };
    }
  };

  // Handle reject button click
  const handleReject = () => {
    // Reset reason and open confirmation dialog
    setRejectReason("");
    setRejectConfirmOpen(true);
  };

  // Handle reject confirmation
  const handleRejectConfirm = async () => {
    try {
      // Prepare API payload for rejecting all pending cancellations
      const payload = {
        cancelType: "partial_items",
        action: "reject_all",
        restock: false,
        reason: rejectReason || "No reason provided",
        decisions: [] // Empty array - reject all pending cancellations
      };
      
      // Call API
      const response = await axiosInstance.put(
        ApiEndPoints.ORDER.businessHandleCancellation(orderId),
        payload
      );
      
      if (response.data?.status === "success") {
        toast.success("cancellation request rejected  ");
        
        // Always deactivate delete icon (reset checkboxes and selection) BEFORE refetch
        // Keep autoActivatedRef.current = true to prevent useEffect from re-activating after refetch
        setShowCheckboxes(false);
        setSelectedItems(new Set());
        
        // Refetch order data to update table
        await refetch();
      } else {
        toast.error("failed to reject cancellation request");
      }
    } catch (error) {
      console.error("Error rejecting cancellation:", error);
      toast.error(error.response?.data?.message || "failed to reject cancellation request");
    } finally {
      // Close confirmation dialog
      setRejectConfirmOpen(false);
    }
  };

  // Handle reject cancel
  const handleRejectCancel = () => {
    setRejectConfirmOpen(false);
  };

  // Handle cancel button click - open business cancel dialog
  const handleCancel = () => {
    // Generate decisions array from selected items
    const selectedItemsData = generateSelectedItemsData();
    
    // Open the new business cancel dialog
    setBusinessCancelDialogData({
      selectedItems: Array.from(selectedItems),
      totalSelectedCount: selectedItems.size,
      selectedItemsData: selectedItemsData
    });
    setBusinessCancelDialogOpen(true);
  };

  // Generate decisions array from selected items (EXCLUDE APPROVED ITEMS)
  const generateSelectedItemsData = () => {
    if (!orderData || selectedItems.size === 0) return [];
    
    const decisions = [];
    const allOrderItems = orderData?.groupOrder?.userOrders || [];
    
    // Group selected items by cartItemId and variantName
    const groupedItems = new Map();
    
    allOrderItems.forEach((userOrder) => {
      const userCart = userOrder.cart || [];
      userCart.forEach((item) => {
        const menuItemId = item.menuItemId?._id;
        if (!menuItemId) return;
        
        if (item.variantEnabled && item.variants) {
          // For variant items
          item.variants.forEach((variant) => {
            const variantKey = `${item._id}-${variant.variantName}`;
            const selectedVariantItems = [];
            
            variant.items?.forEach(variantItem => {
              const itemKey = `${menuItemId}-${variant.variantName}-${variantItem._id}`;
              
              // EXCLUDE APPROVED ITEMS - Only include pending_approval
              if (selectedItems.has(itemKey) && !isItemApproved(item, variant.variantName, variantItem._id)) {
                selectedVariantItems.push(variantItem._id);
              }
            });
            
            if (selectedVariantItems.length > 0) {
              groupedItems.set(variantKey, {
                cartItemId: item._id,
                variantName: variant.variantName,
                itemIds: selectedVariantItems,
                quantityToCancel: selectedVariantItems.length,
                cancellationType: "partial"
              });
            }
          });
        } else {
          // For non-variant items
          const selectedItemIds = [];
          item.items?.forEach(subItem => {
            const itemKey = `${menuItemId}-${subItem._id}`;
            
            // EXCLUDE APPROVED ITEMS - Only include pending_approval
            if (selectedItems.has(itemKey) && !isItemApproved(item, null, subItem._id)) {
              selectedItemIds.push(subItem._id);
            }
          });
          
          if (selectedItemIds.length > 0) {
            groupedItems.set(item._id, {
              cartItemId: item._id,
              itemIds: selectedItemIds,
              quantityToCancel: selectedItemIds.length,
              cancellationType: "partial"
            });
          }
        }
      });
    });
    
    return Array.from(groupedItems.values());
  };

  // Handle business cancel dialog submit (new dialog)
  const handleNewBusinessCancelSubmit = async (payload) => {
    try {
      // Call API
      const response = await axiosInstance.put(
        ApiEndPoints.ORDER.businessHandleCancellation(orderId),
        payload
      );
      
      if (response.data?.status === "success") {
        toast.success("cancellation request processed  ");
        
        // Always deactivate delete icon (reset checkboxes and selection) BEFORE refetch
        // Keep autoActivatedRef.current = true to prevent useEffect from re-activating after refetch
        setShowCheckboxes(false);
        setSelectedItems(new Set());
        
        // Refetch order data to update table
        await refetch();
      } else {
        toast.error("failed to process cancellation request");
      }
    } catch (error) {
      console.error("Error processing cancellation:", error);
      toast.error(error.response?.data?.message || "failed to process cancellation request");
    }
  };

  // Check if there are any selected items
  const hasSelectedItems = () => {
    return selectedItems.size > 0;
  };

  // Check if order has any refunds
  const hasRefunds = () => {
    if (!orderData) return false;
    
    const allOrderItems = orderData?.groupOrder?.userOrders || [];
    
    for (const userOrder of allOrderItems) {
      const userCart = userOrder.cart || [];
      for (const item of userCart) {
        if (item.refunds && item.refunds.length > 0) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Check if the current user (who is the business owner) also has a pending split evenly
  // payment as a customer participant in this order. If so, show a pay button.
  const shouldShowOwnerPayButton = () => {
    if (!orderData || !onPayAsCustomer) return false;
    const currentUserId = currentUser?.userId ?? storedUserId;
    if (!currentUserId) return false;
    const userOrders = orderData?.groupOrder?.userOrders || [];
    const ownerUserOrder = userOrders.find(
      (uo) => uo.userId?._id === currentUserId || uo.userId?._id?.toString() === currentUserId
    );
    if (!ownerUserOrder) return false;
    // Show pay button if owner's payment type is split_evenly and payment is still pending
    const isSplitEvenly =
      ownerUserOrder.paymentType === "split_evenly" ||
      ownerUserOrder.paymentFlow?.type === "split_evenly" ||
      orderData?.paymentFlow?.type === "split_evenly";
    const isPending = ownerUserOrder.paymentStatus === "pending";
    const hasAmountDue = (ownerUserOrder.amountToPay || 0) > (ownerUserOrder.amountPaid || 0);
    return isSplitEvenly && isPending && hasAmountDue;
  };

  // Check if refund button should be enabled
  const shouldShowRefundButton = () => {
    if (!orderData) return false;
    
    // Don't show refund button if there are already refunds
    if (hasRefunds()) return false;
    
    const conditions = {
      isClosedOrder: orderData.orderStatus === "closed order",
      totalPaidPositive: Number(orderData.totalPaidAmount) > 0,
      anyUserPaid: Array.isArray(orderData?.groupOrder?.userOrders)
        && orderData.groupOrder.userOrders.some(u => Number(u?.amountPaid) > 0)
    };
    
    return conditions.isClosedOrder && conditions.totalPaidPositive && conditions.anyUserPaid;
  };

  // Helper function to check if item is cancelled (approved)
  const isItemCancelled = (item, variantName, itemId) => {
    if (!item || !orderData) return false;
    
    if (item.variantEnabled && variantName && item.variants) {
      const variant = item.variants.find(v => v.variantName === variantName);
      if (variant && variant.items) {
        const variantItem = variant.items.find(vi => vi._id === itemId);
        return variantItem?.cancellation?.status === "approved";
      }
    } else if (!item.variantEnabled && item.items) {
      const subItem = item.items.find(si => si._id === itemId);
      return subItem?.cancellation?.status === "approved";
    }
    
    return false;
  };

  // Helper function to check if item is refunded (from refundRequest flag OR refunds array)
  const isItemRefunded = (item, variantName, itemId) => {
    if (!item || !orderData) return false;
    
    // First check refundRequest flag (for pending/completed refunds)
    if (item.variantEnabled && variantName && item.variants) {
      const variant = item.variants.find(v => v.variantName === variantName);
      if (variant && variant.items) {
        const variantItem = variant.items.find(vi => vi._id === itemId);
        if (variantItem?.refundRequest === true) {
          return true;
        }
      }
    } else if (!item.variantEnabled && item.items) {
      const subItem = item.items.find(si => si._id === itemId);
      if (subItem?.refundRequest === true) {
        return true;
      }
    }
    
    // Then check refunds array (for confirmed refunds from API)
    if (item.refunds && Array.isArray(item.refunds)) {
      for (const refund of item.refunds) {
        if (refund.itemIds && Array.isArray(refund.itemIds)) {
          // For variant items, check if variantName matches
          if (item.variantEnabled && variantName) {
            if (refund.variantName === variantName && refund.itemIds.includes(itemId)) {
              return true;
            }
          } else if (!item.variantEnabled) {
            // For non-variant items, just check if itemId is in the refund list
            if (refund.itemIds.includes(itemId)) {
              return true;
            }
          }
        }
      }
    }
    
    return false;
  };

  // Helper function to get all paid items (excluding cancelled approved items)
  const getPaidItems = () => {
    if (!orderData) return [];
    
    const paidItems = [];
    const allOrderItems = orderData?.groupOrder?.userOrders || [];
    
    allOrderItems.forEach((userOrder) => {
      const userCart = userOrder.cart || [];
      userCart.forEach((item) => {
        const menuItemId = item.menuItemId?._id;
        if (!menuItemId) return;
        
        if (item.variantEnabled && item.variants) {
          item.variants.forEach((variant) => {
            variant.items?.forEach(variantItem => {
              // Exclude cancelled (approved) and already refunded items
              if (!isItemCancelled(item, variant.variantName, variantItem._id) &&
                  !isItemRefunded(item, variant.variantName, variantItem._id)) {
                paidItems.push({
                  userOrder,
                  item,
                  variant,
                  variantItem,
                  isVariant: true,
                  menuItemId,
                  variantName: variant.variantName,
                  itemId: variantItem._id,
                  itemKey: `${menuItemId}-${variant.variantName}-${variantItem._id}`
                });
              }
            });
          });
        } else {
          item.items?.forEach(subItem => {
            // Exclude cancelled (approved) and already refunded items
            if (!isItemCancelled(item, null, subItem._id) &&
                !isItemRefunded(item, null, subItem._id)) {
              paidItems.push({
                userOrder,
                item,
                variant: null,
                variantItem: null,
                subItem,
                isVariant: false,
                menuItemId,
                variantName: null,
                itemId: subItem._id,
                itemKey: `${menuItemId}-${subItem._id}`
              });
            }
          });
        }
      });
    });
    
    return paidItems;
  };

  // Handle refund button click - enter refund mode
  const handleRefund = () => {
    setRefundMode(true);
    setShowCheckboxes(true);
    // Pre-select all paid items (excluding cancelled)
    const paidItems = getPaidItems();
    const initialSelected = new Set(paidItems.map(p => p.itemKey));
    setRefundSelectedItems(initialSelected);
  };

  // Calculate total refund amount from selected items
  const calculateRefundAmount = (selectedItemKeys) => {
    if (!orderData) return 0;
    
    let total = 0;
    const allOrderItems = orderData?.groupOrder?.userOrders || [];
    
    allOrderItems.forEach((userOrder) => {
      const userCart = userOrder.cart || [];
      userCart.forEach((item) => {
        const menuItemId = item.menuItemId?._id;
        if (!menuItemId) return;
        
        if (item.variantEnabled && item.variants) {
          item.variants.forEach((variant) => {
            variant.items?.forEach(variantItem => {
              const itemKey = `${menuItemId}-${variant.variantName}-${variantItem._id}`;
              
              // Only include if selected and not cancelled and not already refunded
              if (selectedItemKeys.has(itemKey) &&
                  !isItemCancelled(item, variant.variantName, variantItem._id) &&
                  !isItemRefunded(item, variant.variantName, variantItem._id)) {
                let itemPrice = variantItem.variantPrice || (item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0);
                
                // Add sides prices
                if (variantItem.customizations?.sides) {
                  variantItem.customizations.sides.forEach(side => {
                    itemPrice += (side.price || 0) * (side.quantity || 1);
                  });
                }
                
                total += itemPrice;
              }
            });
          });
        } else {
          item.items?.forEach(subItem => {
            const itemKey = `${menuItemId}-${subItem._id}`;
            
            // Only include if selected and not cancelled and not already refunded
            if (selectedItemKeys.has(itemKey) &&
                !isItemCancelled(item, null, subItem._id) &&
                !isItemRefunded(item, null, subItem._id)) {
              let itemPrice = (item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0);
              
              // Add sides prices
              if (subItem.customizations?.sides) {
                subItem.customizations.sides.forEach(side => {
                  itemPrice += (side.price || 0) * (side.quantity || 1);
                });
              }
              
              total += itemPrice;
            }
          });
        }
      });
    });
    
    return total;
  };

  // Handle refund item selection (minus icon click)
  const handleRefundItemClick = (itemData) => {
    // Get refunded item ids
    const refundedItemIds = [];
    
    if (itemData.isVariant && itemData.variant) {
      itemData.variant.items?.forEach(variantItem => {
        if (variantItem.refundRequest === true) {
          refundedItemIds.push(variantItem._id);
        }
      });
    } else if (!itemData.isVariant && itemData.item) {
      itemData.item.items?.forEach(subItem => {
        if (subItem.refundRequest === true) {
          refundedItemIds.push(subItem._id);
        }
      });
    }
    
    setBusinessRefundSelectionDialogData({
      ...itemData,
      refundedItemIds: refundedItemIds
    });
    
    // Initialize temp selection with currently selected items for this item/variant
    const tempSelection = new Set();
    itemData.allItemIds.forEach(itemId => {
      const itemKey = itemData.isVariant 
        ? `${itemData.menuItemId}-${itemData.variantName}-${itemId}`
        : `${itemData.menuItemId}-${itemId}`;
      
      // Only add to temp selection if it's selected AND not refunded AND not cancelled
      if (refundSelectedItems.has(itemKey) && !refundedItemIds.includes(itemId)) {
        const isCancelled = itemData.isVariant
          ? isItemCancelled(itemData.item, itemData.variantName, itemId)
          : isItemCancelled(itemData.item, null, itemId);
        
        if (!isCancelled) {
          tempSelection.add(itemKey);
        }
      }
    });
    
    setTempRefundSelection(tempSelection);
    setBusinessRefundSelectionDialogOpen(true);
  };

  // Handle refund selection dialog submit
  const handleRefundSelectionSubmit = () => {
    // Update main refundSelectedItems with temp selection
    const newSelectedItems = new Set(refundSelectedItems);
    
    // First, remove all items for this item/variant from main selection
    businessRefundSelectionDialogData.allItemIds.forEach(itemId => {
      const itemKey = businessRefundSelectionDialogData.isVariant 
        ? `${businessRefundSelectionDialogData.menuItemId}-${businessRefundSelectionDialogData.variantName}-${itemId}`
        : `${businessRefundSelectionDialogData.menuItemId}-${itemId}`;
      newSelectedItems.delete(itemKey);
    });
    
    // Then add items from temp selection
    tempRefundSelection.forEach(itemKey => {
      newSelectedItems.add(itemKey);
    });
    
    setRefundSelectedItems(newSelectedItems);
    
    // Close dialog
    setBusinessRefundSelectionDialogOpen(false);
    setBusinessRefundSelectionDialogData(null);
    setTempRefundSelection(new Set());
  };

  // Generate refund decisions array from selected items
  const generateRefundDecisions = (selectedUserId = null) => {
    if (!orderData || refundSelectedItems.size === 0) return [];
    
    const decisions = [];
    const allOrderItems = orderData?.groupOrder?.userOrders || [];
    
    // Group selected items by cartItemId and variantName
    const groupedItems = new Map();
    
    allOrderItems.forEach((userOrder) => {
      const userCart = userOrder.cart || [];
      userCart.forEach((item) => {
        const menuItemId = item.menuItemId?._id;
        if (!menuItemId) return;
        
        if (item.variantEnabled && item.variants) {
          // For variant items
          item.variants.forEach((variant) => {
            const variantKey = `${item._id}-${variant.variantName}`;
            const selectedVariantItems = [];
            
            variant.items?.forEach(variantItem => {
              const itemKey = `${menuItemId}-${variant.variantName}-${variantItem._id}`;
              
              // Only include if selected, not cancelled, and not already refunded
              if (refundSelectedItems.has(itemKey) && 
                  !isItemCancelled(item, variant.variantName, variantItem._id) &&
                  !isItemRefunded(item, variant.variantName, variantItem._id)) {
                selectedVariantItems.push(variantItem._id);
              }
            });
            
            if (selectedVariantItems.length > 0) {
              const isFullRefund = selectedVariantItems.length === variant.items?.length;
              
              groupedItems.set(variantKey, {
                cartItemId: item._id,
                refundType: isFullRefund ? "full" : "partial",
                quantityToRefund: selectedVariantItems.length,
                itemIdsToRefund: selectedVariantItems,
                variantName: variant.variantName,
                note: variant.note || ""
              });
            }
          });
        } else {
          // For non-variant items
          const selectedItemIds = [];
          
          item.items?.forEach(subItem => {
            const itemKey = `${menuItemId}-${subItem._id}`;
            
            // Only include if selected, not cancelled, and not already refunded
            if (refundSelectedItems.has(itemKey) && 
                !isItemCancelled(item, null, subItem._id) &&
                !isItemRefunded(item, null, subItem._id)) {
              selectedItemIds.push(subItem._id);
            }
          });
          
          if (selectedItemIds.length > 0) {
            const isFullRefund = selectedItemIds.length === item.items?.length;
            
            groupedItems.set(item._id, {
              cartItemId: item._id,
              refundType: isFullRefund ? "full" : "partial",
              quantityToRefund: selectedItemIds.length,
              itemIdsToRefund: selectedItemIds,
              variantName: null,
              note: item.note || ""
            });
          }
        }
      });
    });
    
    return Array.from(groupedItems.values());
  };

  // Handle refund dialog button click
  const handleRefundDialogClick = () => {
    // Generate initial decisions without userId (will be added when user selects)
    const selectedItemsData = generateRefundDecisions();
    const totalAmount = calculateRefundAmount(refundSelectedItems);
    
    setBusinessRefundDialogData({
      selectedItems: Array.from(refundSelectedItems),
      totalSelectedCount: refundSelectedItems.size,
      selectedItemsData: selectedItemsData
    });
    setBusinessRefundDialogOpen(true);
  };

  // Handle select all items for full refund
  const handleSelectAllItemsForRefund = () => {
    // Select all paid items (excluding cancelled)
    const paidItems = getPaidItems();
    const allPaidItemKeys = new Set(paidItems.map(p => p.itemKey));
    setRefundSelectedItems(allPaidItemKeys);
  };

  // Update dialog data when refundSelectedItems changes and dialog is open
  useEffect(() => {
    if (businessRefundDialogOpen && refundMode) {
      // Generate decisions without userId (will be added when user selects in dialog)
      const selectedItemsData = generateRefundDecisions();
      setBusinessRefundDialogData({
        selectedItems: Array.from(refundSelectedItems),
        totalSelectedCount: refundSelectedItems.size,
        selectedItemsData: selectedItemsData
      });
    }
  }, [refundSelectedItems, businessRefundDialogOpen]);

  // Handle refund API submit
  const handleRefundSubmit = async (payload, selectedUserId) => {
    try {
      const response = await axiosInstance.put(
        ApiEndPoints.ORDER.handleRefund(orderId),
        payload
      );
      
      if (response.data?.status === "success") {
      toast.success("refund processed  ");
        setBusinessRefundDialogOpen(false);
        
        // Reset refund mode
        setRefundMode(false);
        setShowCheckboxes(false);
        setRefundSelectedItems(new Set());
        
        // Refetch order data to update table
        await refetch();
      } else {
        toast.error("failed to process refund");
      }
    } catch (error) {
      console.error("Error processing refund:", error);
      toast.error(error.response?.data?.message || "failed to process refund");
      // ensure refund dialog stays open on error
      setBusinessRefundDialogOpen(true);
    }
  };

  // Format time ago
  const getTimeAgo = (dateString) => {
    return moment(dateString).fromNow();
  };

  // Helper function to get initials from user name
  const getUserInitials = (user) => {
    try {
      if (user?.fullName && user.fullName.trim()) {
        const names = user.fullName.trim().split(' ').filter(name => name.length > 0);
        if (names.length === 1) {
          return names[0].charAt(0).toUpperCase();
        } else if (names.length >= 2) {
          return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
        }
      }
      if (user?.userName && user.userName.trim()) {
        return user.userName.charAt(0).toUpperCase();
      }
      if (user?.userFullName && user.userFullName.trim()) {
        const names = user.userFullName.trim().split(' ').filter(name => name.length > 0);
        if (names.length === 1) {
          return names[0].charAt(0).toUpperCase();
        } else if (names.length >= 2) {
          return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
        }
      }
      return '?';
    } catch (error) {
      return '?';
    }
  };

  // Helper function to get unique group members from order data
  // This gets all unique users from userOrders, but if all items are reassigned to one user,
  // we still need to show all possible users. We'll use a combination approach.
  const getGroupMembers = () => {
    if (!orderData?.groupOrder?.userOrders) {
      return [];
    }

    const aiUserId = process.env.REACT_APP_AI_USER_ID;

    // Get unique users based on userId from all userOrders
    const uniqueUsers = [];
    const seenUserIds = new Set();

    orderData.groupOrder.userOrders.forEach((userOrder) => {
      // Extract userId - it can be an object with _id or a string
      const userId = userOrder.userId?._id || userOrder.userId;
      
      // Normalize userId to string for consistent comparison
      const normalizedUserId = userId ? String(userId) : null;
      
      // Skip if we've already seen this user or if it's beemz ai
      if (!normalizedUserId || seenUserIds.has(normalizedUserId) || normalizedUserId === aiUserId) {
        return;
      }

      seenUserIds.add(normalizedUserId);

      uniqueUsers.push({
        userId: normalizedUserId,
        fullName: userOrder.userFullName || userOrder.userId?.fullName || userOrder.userName || 'Unknown User',
        userName: userOrder.userName || userOrder.userId?.userName || '',
        profilePic: userOrder.userProfilePic || userOrder.userId?.image?.profilePic || '',
        amountPaid: userOrder.amountPaid || 0,
        subtotal: userOrder.subtotal || 0,
      });
    });

    return uniqueUsers;
  };

  // Check if this is a group order with multiple users
  // Use the number of userOrders to determine if it's a group order,
  // not just the number of unique users (since after reassignment, all might have same user)
  const isGroupOrder = () => {
    return orderData?.groupOrder?.userOrders?.length > 1;
  };

  // Get all possible group members for dropdown
  // Only use users from groupOrder.userOrders (not from conversationInfo)
  const getAllGroupMembersForDropdown = () => {
    const aiUserId = process.env.REACT_APP_AI_USER_ID;
    
    // If we have stored original group members and it's a group order, use those
    // This ensures we show all users even after reassignment
    if (isGroupOrder() && allOriginalGroupMembers.length > 0) {
      // Filter out beemz ai from original group members
      return allOriginalGroupMembers.filter(member => {
        const memberId = member.userId;
        return memberId !== aiUserId;
      });
    }
    
    // Otherwise return current unique users from userOrders (already filtered in getGroupMembers)
    return getGroupMembers();
  };

  // Helper function to render customer cell with dropdown
  const renderCustomerCell = (userOrder, itemIndex, cartId, options = {}) => {
    const { avatarOpacity, textDecoration, color, ...sx } = options;
    const currentUserId = userOrder.userId?._id || userOrder.userId;
    const normalizedCurrentUserId = currentUserId ? String(currentUserId) : null;
    const groupMembers = getAllGroupMembersForDropdown();
    // Show dropdown only when in edit mode and no items are selected for bulk assignment
    const showDropdown = editMode && selectedItemsForBulkAssignment.size === 0;
    // Disable if this cartId is selected for bulk assignment
    const isDisabled = selectedItemsForBulkAssignment.has(cartId);

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ...sx }}>
        <Avatar 
          src={`${IMAGE_URL}${userOrder.userProfilePic || ""}`} 
          sx={{ width: 24, height: 24, ...(avatarOpacity !== undefined ? { opacity: avatarOpacity } : {}) }}
        >
          {userOrder.userFullName?.charAt(0) || "U"}
        </Avatar>
        {showDropdown ? (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={normalizedCurrentUserId || ""}
              disabled={isDisabled}
              onChange={(e) => {
                const newUserId = e.target.value;
                if (normalizedCurrentUserId && newUserId && cartId) {
                  handleItemUserChange(normalizedCurrentUserId, newUserId, userOrder, cartId);
                }
              }}
              renderValue={(selected) => {
                if (!selected) return "";
                const selectedMember = groupMembers.find(m => String(m.userId) === String(selected));
                return selectedMember ? selectedMember.fullName.toLowerCase() : userOrder.userFullName?.toLowerCase() || "user";
              }}
              displayEmpty
              sx={{
                height: 24,
                fontSize: '0.7rem',
                textTransform: 'lowercase',
                backgroundColor: '#f0f4ff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#7C3AED',
                  borderWidth: '1px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#7C3AED',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#7C3AED',
                },
                '& .MuiSelect-select': {
                  padding: '4px 8px',
                  fontSize: '0.7rem',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 200,
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#FFFFFF',
                    mt: 0.5,
                    zIndex: '20000 !important',
                    '& .MuiMenuItem-root': {
                      textTransform: 'lowercase',
                      fontSize: '0.75rem',
                      color: '#1F2937',
                      minHeight: 32,
                      padding: '6px 12px',
                      '&:hover': {
                        backgroundColor: '#f0f4ff',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#f0f4ff',
                        '&:hover': {
                          backgroundColor: '#f0f4ff',
                        },
                      },
                    },
                  },
                  style: {
                    zIndex: 20000,
                  },
                },
                PopoverClasses: {
                  root: 'group-members-popover',
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                disablePortal: false,
                container: typeof document !== 'undefined' ? document.body : null,
              }}
            >
              {groupMembers.map((member, index) => (
                <MenuItem key={member.userId || index} value={String(member.userId)}>
                  {member.fullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography 
            variant="caption" 
            sx={{ 
              textTransform: "lowercase",
              ...(textDecoration ? { textDecoration } : {}),
              ...(color ? { color } : {})
            }}
          >
            {userOrder.userFullName || "user"}
          </Typography>
        )}
      </Box>
    );
  };

  // Handle user selection change in dropdown
  const handleUserChange = async (event) => {
    const newUserId = event.target.value;
    const conversationId = orderData?.conversationId;
    
    if (!newUserId || !conversationId) {
      return;
    }
    
    // Don't make API call if selecting the same user
    if (selectedUserId === newUserId) {
      return;
    }
    
    try {
      const response = await axiosInstance.put(
        ApiEndPoints.ORDER.updateUser(conversationId),
        { newUserId }
      );
      
      if (response.data?.status === "success") {
        setSelectedUserId(newUserId);
        toast.success("user updated  ");
        // Optionally refetch order data to get updated information
        await refetch();
      } else {
        toast.error(response.data?.message || "failed to update user");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(error.response?.data?.message || "failed to update user");
    }
  };

  // Handle item-level user change (for individual items in table)
  const handleItemUserChange = async (oldUserId, newUserId, userOrder, cartId) => {
    const conversationId = orderData?.conversationId;
    const userOrderId = userOrder?._id;
    
    if (!newUserId || !conversationId || !userOrderId || !cartId) {
      return;
    }
    
    // Don't make API call if selecting the same user
    if (String(oldUserId) === String(newUserId)) {
      return;
    }
    
    try {
      const response = await axiosInstance.put(
        ApiEndPoints.ORDER.updateGroupOrderUser(conversationId),
        { userOrderId, newUserId, cartId }
      );
      
      if (response.data?.status === "success") {
        toast.success("item user updated  ");
        // Refetch order data to get updated information
        await refetch();
      } else {
        toast.error(response.data?.message || "failed to update item user");
      }
    } catch (error) {
      console.error("Error updating item user:", error);
      toast.error(error.response?.data?.message || "failed to update item user");
    }
  };

  // Handle bulk customer assignment for multiple items
  const handleBulkCustomerAssignment = async (newUserId) => {
    const conversationId = orderData?.conversationId;
    
    if (!newUserId || !conversationId || selectedItemsForBulkAssignment.size === 0) {
      return;
    }
    
    // Convert Set to Array of cartIds
    const cartIds = Array.from(selectedItemsForBulkAssignment);
    
    try {
      const response = await axiosInstance.put(
        ApiEndPoints.ORDER.updateGroupOrderUser(conversationId),
        { newUserId, cartId: cartIds }
      );
      
      if (response.data?.status === "success") {
        toast.success("items assigned to customer  ");
        // Clear selection and refetch order data
        setSelectedItemsForBulkAssignment(new Set());
        await refetch();
      } else {
        toast.error(response.data?.message || "failed to assign items to customer");
      }
    } catch (error) {
      console.error("Error assigning items to customer:", error);
      toast.error(error.response?.data?.message || "failed to assign items to customer");
    }
  };

  // Calculate tax breakdown from order data
  const calculateTaxBreakdown = () => {
    if (!orderData?.taxes || orderData.taxes.length === 0) {
      return { 
        subtotalBeforeTax: orderData?.groupSubtotal || 0, 
        taxBreakdown: [], 
        totalTax: 0 
      };
    }

    const subtotal = orderData?.groupSubtotal || 0;
    const taxBreakdown = [];
    let totalCalculatedTax = 0;

    orderData.taxes.forEach((taxGroup) => {
      taxGroup.taxRates.forEach((taxRate) => {
        // Only include tax rates that are greater than 0
        if (taxRate.rate > 0) {
          const calculatedAmount = (subtotal * taxRate.rate) / 100;
          taxBreakdown.push({
            type: taxRate.type.toUpperCase(),
            rate: taxRate.rate,
            amount: calculatedAmount,
          });
          totalCalculatedTax += calculatedAmount;
        }
      });
    });

    // Subtotal before tax = total - tax
    const subtotalBeforeTax = subtotal - totalCalculatedTax;

    return {
      subtotalBeforeTax: subtotalBeforeTax,
      taxBreakdown: taxBreakdown,
      totalTax: totalCalculatedTax,
    };
  };

  // Helper function to get cancelled item ids from API data (INDIVIDUAL ITEMS ONLY)
  // Returns object with pending and approved arrays
  // NOTE: Items with status "rejected" are treated as normal items (not cancelled)
  const getCancelledItemIds = (item, variantName = null) => {
    if (!item) return { pending: [], approved: [] };
    
    const pendingIds = [];
    const approvedIds = [];
    
    if (item.variantEnabled && variantName && item.variants) {
      // For variant items, get cancelled items for specific variant
      const variant = item.variants.find(v => v.variantName === variantName);
      if (variant && variant.items) {
        variant.items.forEach(variantItem => {
          if (variantItem.cancellation?.requested && variantItem.cancellation.status !== "rejected") {
            if (variantItem.cancellation.status === "pending_approval") {
              pendingIds.push(variantItem._id);
            } else if (variantItem.cancellation.status === "approved") {
              approvedIds.push(variantItem._id);
            }
          }
        });
      }
    } else if (!item.variantEnabled && item.items) {
      // For non-variant items
      item.items.forEach(subItem => {
        if (subItem.cancellation?.requested && subItem.cancellation.status !== "rejected") {
          if (subItem.cancellation.status === "pending_approval") {
            pendingIds.push(subItem._id);
          } else if (subItem.cancellation.status === "approved") {
            approvedIds.push(subItem._id);
          }
        }
      });
    }
    
    return { 
      pending: pendingIds,
      approved: approvedIds
    };
  };

  // Helper function to get refunded item ids from API data (from refunds array)
  // Returns array of refunded item IDs
  const getRefundedItemIds = (item, variantName = null) => {
    if (!item || !item.refunds || !Array.isArray(item.refunds)) return [];
    
    const refundedIds = [];
    
    // Iterate through all refunds
    item.refunds.forEach(refund => {
      if (refund.itemIds && Array.isArray(refund.itemIds)) {
        // For variant items, check if variantName matches
        if (item.variantEnabled && variantName) {
          if (refund.variantName === variantName) {
            refundedIds.push(...refund.itemIds);
          }
        } else if (!item.variantEnabled) {
          // For non-variant items, add all refunded item IDs
          refundedIds.push(...refund.itemIds);
        }
      }
    });
    
    // Remove duplicates
    return [...new Set(refundedIds)];
  };

  // Helper function to get total quantity
  const getTotalQuantity = (item, variantName = null) => {
    if (item.variantEnabled && variantName && item.variants) {
      const variant = item.variants.find(v => v.variantName === variantName);
      return variant?.items?.length || 0;
    }
    return item.quantity || 0;
  };

  // Helper function to check if item has notes
  const hasItemNotes = (item) => {
    if (item.variantEnabled && item.variants) {
      return item.variants.some(variant => variant.note && variant.note.trim() !== "");
    }
    return item.note && item.note.trim() !== "";
  };

  // Helper function to get all notes
  const getAllNotes = (item) => {
    if (item.variantEnabled && item.variants) {
      return item.variants
        .filter(variant => variant.note && variant.note.trim() !== "")
        .map(variant => `${variant.variantName}: ${variant.note}`)
        .join(', ');
    }
    return item.note || "";
  };

  // Helper function to check if item has customizations
  const hasItemCustomizations = (item) => {
    if (item.variantEnabled && item.variants) {
      return item.variants.some(variant =>
        variant.items?.some(variantItem =>
          variantItem.customizations?.ingredientModifications?.some(mod => mod.action !== "no_modification")
        )
      );
    }
    return item.items?.some(subItem =>
      subItem.customizations?.ingredientModifications?.some(mod => mod.action !== "no_modification")
    );
  };


  // Toggle sides expansion
  const toggleSidesExpansion = (itemKey) => {
    setExpandedSides((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  // Filter order items based on search term (client-side filtering)
  const filterOrderItems = (items) => {
    if (!searchTerm.trim()) return items;
    
    const searchLower = searchTerm.toLowerCase();
    return items.filter((userOrder) => {
      const userCart = userOrder.cart || [];
      return userCart.some((item) => {
        // Search in item name
        if (item.menuItemId?.name?.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search in item code
        if (item.menuItemId?.code?.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search in user name
        if (userOrder.userFullName?.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search in notes
        if (item.note?.toLowerCase().includes(searchLower)) {
          return true;
        }
        return false;
      });
    });
  };

  // Get background color based on item status
  const getStatusBackgroundColor = (status) => {
    const statusColorMap = {
      "new items": "#FFC4C4",
      "confirmed items": "#F0F0AD",
      "items being prepared": "#FFD1F1",
      "ready to serve": "#ADF0D2",
      "collected": "#ADF0D2",
      "ready to pick-up": "#ADF0D2",
      "ready to deliver": "#ADF0D2",
      "cancelled items": "#CFCFCF",
      "declined items": "#CFCFCF"
    };
    return statusColorMap[status?.toLowerCase()] || "#FFFFFF"; // Default to white if status not found
  };

  // Helper function to get all sub-items (regular or variant)
  const getSubItems = (item) => {
    if (item.variantEnabled && item.variants) {
      return item.variants.flatMap(variant => 
        variant.items?.map(variantItem => ({
          ...variantItem,
          variantName: variant.variantName,
          variantNote: variant.note
        })) || []
      );
    }
    return item.items || [];
  };

  // Check if item should show expand arrow
  const shouldShowExpandArrow = (item) => {
    const subItems = getSubItems(item);
    return subItems.some(subItem => {
      const hasSides = subItem.customizations?.sides && subItem.customizations.sides.length > 0;
      const hasCustomizations = subItem.customizations?.ingredientModifications?.some(mod => mod.action !== "no_modification");
      const hasNotes = subItem.variantNote && subItem.variantNote.trim() !== "";
      
      return hasSides || hasCustomizations || hasNotes;
    });
  };

  // Reusable component for rendering sub-items for a specific variant
  const renderVariantSubItems = (variant, variantKey, cancelledItemIds = []) => {
    const variantItems = variant.items || [];
    
    // Filter out cancelled items - only show remaining items
    const remainingItems = variantItems.filter(variantItem => 
      !cancelledItemIds.includes(variantItem._id)
    );
    
    return remainingItems.map((variantItem, itemIndex) => {
      const subItemKey = `${variantKey}-item-${itemIndex}`;
      const hasSides = variantItem.customizations?.sides && variantItem.customizations.sides.length > 0;
      const hasCustomizations = variantItem.customizations?.ingredientModifications?.some(mod => mod.action !== "no_modification");

      return (
        <TableRow key={subItemKey} sx={{ backgroundColor: "#f9f9f9" }}>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell sx={{ pl: 4 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "lowercase" }}>
                  item {variantItem.itemNumber}
                </Typography>
                {hasCustomizations && (
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, textTransform: "lowercase" }}>
                          customizations:
                        </Typography>
                        {variantItem.customizations.ingredientModifications
                          .filter(mod => mod.action !== "no_modification")
                          .map((mod, modIndex) => {
                            let text = "";
                            if (mod.action === "substitute") {
                              text = `Substitute ${mod.ingredientName} with ${mod.substituteWith}`;
                            } else if (mod.action === "remove") {
                              text = `Remove ${mod.ingredientName}`;
                            } else if (mod.action === "add") {
                              text = `Add extra ${mod.ingredientName}`;
                            }
                            return (
                              <Typography key={modIndex} variant="caption" sx={{ display: "block", textTransform: "lowercase", mb: 0.5 }}>
                                • {text}
                              </Typography>
                            );
                          })}
                      </Box>
                    }
                    arrow
                    placement="top"
                    PopperProps={{ style: { zIndex: 10001 } }}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: 'white',
                          color: 'black',
                          border: '1px solid #e0e0e0',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          '& .MuiTooltip-arrow': {
                            color: 'white',
                            '&::before': {
                              border: '1px solid #e0e0e0'
                            }
                          }
                        }
                      }
                    }}
                  >
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      <CustomizeIcon size="16" color="#6200EE" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {hasSides && (
                <Box sx={{ mt: 1 }}>
                  {variantItem.customizations.sides.map((side, sideIndex) => (
                    <Typography key={sideIndex} variant="caption" sx={{ display: "block", textTransform: "lowercase", mb: 0.5, pl: 2 }}>
                      {side.sideName} + ${side.price}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
        </TableRow>
      );
    });
  };

  // Reusable component for rendering sub-items (for non-variant items)
  const renderSubItems = (item, itemKey, cancelledItemIds = []) => {
    const subItems = getSubItems(item);
    
    // Filter out cancelled items - only show remaining items
    const remainingItems = subItems.filter(subItem => 
      !cancelledItemIds.includes(subItem._id)
    );
    
    return remainingItems.map((subItem, subIndex) => {
      const subItemKey = `${itemKey}-${subIndex}`;
      const hasSides = subItem.customizations?.sides && subItem.customizations.sides.length > 0;
      const hasCustomizations = subItem.customizations?.ingredientModifications?.some(mod => mod.action !== "no_modification");

      return (
        <TableRow key={subItemKey} sx={{ backgroundColor: "#f9f9f9" }}>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell sx={{ pl: 4 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "lowercase" }}>
                  {subItem.variantName ? `${subItem.variantName} variant` : `item ${subItem.itemNumber || subIndex + 1}`}
                  {subItem.variantName && ` • qty: ${subItem.quantity} • $${subItem.variantPrice} each`}
                </Typography>
                {subItem.variantNote && (
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "lowercase", display: "block", mb: 1 }}>
                    note: {subItem.variantNote}
                  </Typography>
                )}
                {hasCustomizations && (
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, textTransform: "lowercase" }}>
                          customizations:
                        </Typography>
                        {subItem.customizations.ingredientModifications
                          .filter(mod => mod.action !== "no_modification")
                          .map((mod, modIndex) => {
                            let text = "";
                            if (mod.action === "substitute") {
                              text = `Substitute ${mod.ingredientName} with ${mod.substituteWith}`;
                            } else if (mod.action === "remove") {
                              text = `Remove ${mod.ingredientName}`;
                            } else if (mod.action === "add") {
                              text = `Add extra ${mod.ingredientName}`;
                            }
                            return (
                              <Typography key={modIndex} variant="caption" sx={{ display: "block", textTransform: "lowercase", mb: 0.5 }}>
                                • {text}
                              </Typography>
                            );
                          })}
                      </Box>
                    }
                    arrow
                    placement="top"
                    PopperProps={{ style: { zIndex: 10001 } }}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: 'white',
                          color: 'black',
                          border: '1px solid #e0e0e0',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          '& .MuiTooltip-arrow': {
                            color: 'white',
                            '&::before': { border: '1px solid #e0e0e0' }
                          }
                        }
                      }
                    }}
                  >
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      <CustomizeIcon size="16" color="#6200EE" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {hasSides && (
                <Box sx={{ mt: 1 }}>
                  {subItem.customizations.sides.map((side, sideIndex) => (
                    <Typography key={sideIndex} variant="caption" sx={{ display: "block", textTransform: "lowercase", mb: 0.5, pl: 2 }}>
                      {side.sideName} + ${side.price}{side.quantity > 1 ? ` (qty: ${side.quantity})` : ''}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
        </TableRow>
      );
    });
  };

  // Resolve currency symbol from order data
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return "$";
    const currency = currencyData.currencies.find(
      (curr) => curr.code === currencyCode.toUpperCase()
    );
    return currency ? currency.symbol : "$";
  };
  const currencySymbol = getCurrencySymbol(orderData?.currency);

  // Render billing summary table
  const renderBillingSummary = () => {
    if (!orderData) return null;

    const { subtotalBeforeTax, taxBreakdown, totalTax } = calculateTaxBreakdown();
    const total = orderData?.groupSubtotal || 0;
    const totalPaidAmount = orderData?.totalPaidAmount || 0;
    const remainingAmount = orderData?.remainingAmount || 0;
    
    // Get refund summary
    const refundSummary = orderData?.refundSummary || {};
    const totalRefundAmount = refundSummary?.totalRefundAmount || 0;
    
    // Calculate remaining refundable per user
    const perUserRefunds = refundSummary?.perUser || [];

    return (
      <Box sx={{ 
        mt: 3, 
        p: 2, 
        backgroundColor: "#f8f9fa",
        borderRadius: 1,
        border: "1px solid #e5e7eb"
      }}>
        <Typography variant="h6" sx={{ 
          textTransform: "lowercase", 
          fontWeight: 600, 
          mb: 2,
          fontSize: "1rem"
        }}>
          billing summary
        </Typography>

        {/* Subtotal before tax */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          mb: 1,
          pb: 1,
          borderBottom: "1px solid #e5e7eb"
        }}>
          <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
            subtotal:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {currencySymbol} {subtotalBeforeTax.toFixed(2)}
          </Typography>
        </Box>

        {/* Tax breakdown */}
        {taxBreakdown.length > 0 && taxBreakdown.map((tax, index) => (
          <Box key={index} sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            mb: 1,
            pl: 2
          }}>
            <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.secondary" }}>
              +{tax.type.toLowerCase()}({tax.rate}%):
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {currencySymbol} {tax.amount.toFixed(2)}
            </Typography>
          </Box>
        ))}

        {/* Total */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          mb: 1,
          pt: 1,
          pb: 1,
          borderTop: "1px solid #e5e7eb",
          borderBottom: "1px solid #e5e7eb"
        }}>
          <Typography variant="body2" sx={{ textTransform: "lowercase", fontWeight: 600 }}>
            total:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {currencySymbol} {total.toFixed(2)}
          </Typography>
        </Box>

        {/* Paid Amount */}
        {totalPaidAmount > 0 && (
          <Box sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            mb: 1,
            pl: 2
          }}>
            <Typography variant="body2" sx={{ textTransform: "lowercase", color: "success.main" }}>
              paid amount:
            </Typography>
            <Typography variant="body2" sx={{ color: "success.main", fontWeight: 600 }}>
              {currencySymbol} {totalPaidAmount.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Remaining Amount */}
        {remainingAmount > 0 && (
          <Box sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            mb: 1,
            pl: 2
          }}>
            <Typography variant="body2" sx={{ textTransform: "lowercase", color: "warning.main" }}>
              remaining amount:
            </Typography>
            <Typography variant="body2" sx={{ color: "warning.main", fontWeight: 600 }}>
              {currencySymbol} {remainingAmount.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Refund Amount */}
        {totalRefundAmount > 0 && (
          <Box sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            mb: 1,
            pt: 1,
            borderTop: "1px solid #e5e7eb",
            pl: 2
          }}>
            <Typography variant="body2" sx={{ textTransform: "lowercase", color: "error.main" }}>
              refund amount:
            </Typography>
            <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>
              {currencySymbol} {totalRefundAmount.toFixed(2)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Render order items table
  const renderOrderItems = () => {
    // In refund mode, we need to filter and show only paid items (excluding cancelled)
    let allOrderItems = orderData?.groupOrder?.userOrders || orderData?.cart || [];
    
    if (refundMode) {
      // Filter to show only items that are paid (not cancelled approved)
      // This is already handled by the rendering logic below, but we ensure cancelled items are excluded
    }
    
    // Filter items based on search term
    const orderItems = filterOrderItems(allOrderItems);

    if (orderItems.length === 0) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ p: 2, textTransform: "lowercase" }}
        >
          {searchTerm.trim() ? "no items found matching your search" : "no items in this order"}
        </Typography>
      );
    }

    return (
      <ColoredTable component={Paper}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                textTransform: "lowercase",
                backgroundColor: "#E4E4E4 !important",
              }}
            >
              {showCheckboxes && (() => {
                // In refund mode, use refundSelectedItems for select all
                const activeSelectedItems = refundMode ? refundSelectedItems : selectedItems;
                const allSelectableKeys = refundMode 
                  ? getPaidItems().map(p => p.itemKey)
                  : getAllSelectableKeys();
                
                if (allSelectableKeys.length === 0) return null;
                
                const selectedCount = allSelectableKeys.filter(key => activeSelectedItems.has(key)).length;
                const checked = selectedCount === allSelectableKeys.length && allSelectableKeys.length > 0;
                
                return (
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Checkbox
                        checked={checked}
                        indeterminate={false}
                        onChange={() => {
                          if (refundMode) {
                            const newSelectedItems = checked ? new Set() : new Set(allSelectableKeys);
                            setRefundSelectedItems(newSelectedItems);
                          } else {
                            handleSelectAllInTable();
                          }
                        }}
                        size="small"
                        sx={{ p: 0 }}
                      />
                      <Typography variant="caption" sx={{ textTransform: "lowercase" }}>
                        select
                      </Typography>
                    </Box>
                  </TableCell>
                );
              })()}
              {editMode && !showCheckboxes && (
                <TableCell>
                  <Typography variant="caption" sx={{ textTransform: "lowercase" }}>
                    select
                  </Typography>
                </TableCell>
              )}
              <TableCell>item code</TableCell>
              <TableCell>items</TableCell>
              <TableCell>variant</TableCell>
              <TableCell>unit price</TableCell>
              <TableCell>qty</TableCell>
              <TableCell>notes/custom</TableCell>
              <TableCell>customer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderItems.map((userOrder, userIndex) => {
              const userCart = userOrder.cart || [];
              return userCart.map((item, index) => {
                const itemKey = `${userIndex}-${index}`;
                const shouldExpand = shouldShowExpandArrow(item);
                const isExpanded = expandedSides[itemKey];
                
                // For variant-enabled items, show each variant as a separate row
                if (item.variantEnabled && item.variants) {
                  return item.variants.map((variant, variantIndex) => {
                    const variantQuantity = variant.items?.reduce((total, variantItem) => total + (variantItem.quantity || 0), 0) || 0;
                    const variantPrice = variant.items?.[0]?.variantPrice || (item.itemPrice && item.quantity ? item.itemPrice / item.quantity : 0);
                    const variantKey = `${userIndex}-${index}-${variantIndex}`;
                    
                    // Get all item ids for this variant
                    const allItemIds = variant.items?.map(variantItem => variantItem._id) || [];
                    const totalQuantity = allItemIds.length;
                    
                    // Get cancelled items from API data
                    const cancelledData = getCancelledItemIds(item, variant.variantName);
                    const pendingItemIds = cancelledData.pending || [];
                    const approvedItemIds = cancelledData.approved || [];
                    
                    // Get refunded items from API data (from refunds array)
                    const apiRefundedIds = getRefundedItemIds(item, variant.variantName);
                    
                    // In refund mode, filter out cancelled items completely
                    const activeSelectedItems = refundMode ? refundSelectedItems : selectedItems;
                    
                    // Determine display logic based on showCheckboxes state and mode
                    let displayPendingIds, displayApprovedIds, remainingIds, refundedIds, pendingRefundIds;
                    
                    if (refundMode) {
                      // Refund mode: Show only paid items (excluding cancelled approved)
                      // Get already refunded items (refundRequest === true OR from refunds array)
                      refundedIds = allItemIds.filter(itemId => 
                        isItemRefunded(item, variant.variantName, itemId)
                      );
                      
                      // Get pending refund items (selected in refundSelectedItems but not yet refunded)
                      pendingRefundIds = allItemIds.filter(itemId => {
                        const itemKey = `${item.menuItemId?._id}-${variant.variantName}-${itemId}`;
                        return refundSelectedItems.has(itemKey) && !refundedIds.includes(itemId) && !approvedItemIds.includes(itemId);
                      });
                      
                      // Remaining = all items minus (cancelled + pending refund + confirmed refunded)
                      remainingIds = allItemIds.filter(itemId => 
                        !approvedItemIds.includes(itemId) && 
                        !pendingRefundIds.includes(itemId) &&
                        !refundedIds.includes(itemId)
                      );
                      displayPendingIds = [];
                      displayApprovedIds = [];
                    } else if (showCheckboxes) {
                      // Cancellation mode with checkboxes: use selectedItems for pending
                      displayPendingIds = allItemIds.filter(itemId => 
                        selectedItems.has(`${item.menuItemId?._id}-${variant.variantName}-${itemId}`) &&
                        !approvedItemIds.includes(itemId)
                      );
                      displayApprovedIds = approvedItemIds;
                      
                      const allCancelledIds = [...displayPendingIds, ...displayApprovedIds];
                      remainingIds = allItemIds.filter(itemId => 
                        !allCancelledIds.includes(itemId)
                      );
                      refundedIds = [];
                      pendingRefundIds = [];
                    } else {
                      // Normal mode: show API data as-is
                      displayPendingIds = pendingItemIds;
                      displayApprovedIds = approvedItemIds;
                      
                      // Get refunded items from API (from refunds array)
                      refundedIds = apiRefundedIds;
                      
                      const allCancelledIds = [...pendingItemIds, ...approvedItemIds];
                      const allExcludedIds = [...allCancelledIds, ...refundedIds];
                      remainingIds = allItemIds.filter(itemId => 
                        !allExcludedIds.includes(itemId)
                      );
                      pendingRefundIds = [];
                    }
                    
                    const remainingQuantity = remainingIds.length;
                    const pendingQuantity = displayPendingIds.length;
                    const approvedQuantity = displayApprovedIds.length;
                    const refundedQuantity = refundedIds.length;
                    const pendingRefundQuantity = refundMode ? pendingRefundIds.length : 0;
                    const confirmedRefundedQuantity = refundMode ? refundedIds.length : 0;
                    
                   // Show cancelled items in normal view (do not filter entire row out)
                    
                    if (variantQuantity === 0) return null;
                    
                    const rows = [];
                    
                   // ====================
                   // ROW 1: REMAINING ITEMS (No cancellation, no refund)
                   // ====================
                   // In normal view, still render remaining items; cancelled/refunded get their own rows
                    
                    if (remainingQuantity > 0) {
                      const currentStatus = variant.itemStatus || item.itemStatus || "new items";
                      const backgroundColor = getStatusBackgroundColor(currentStatus);
                    
                    rows.push(
                      <TableRow key={variantKey} sx={{ backgroundColor }}>
                        {showCheckboxes && (
                          <TableCell>
                              <Checkbox
                                checked={remainingIds.some(itemId => {
                                  const itemKey = `${item.menuItemId?._id}-${variant.variantName}-${itemId}`;
                                  return refundMode 
                                    ? refundSelectedItems.has(itemKey)
                                    : selectedItems.has(itemKey);
                                })}
                                onChange={() => {
                                  const itemKeys = remainingIds.map(itemId => 
                                    `${item.menuItemId?._id}-${variant.variantName}-${itemId}`
                                  );
                                  
                                  if (refundMode) {
                                    const newSelectedItems = new Set(refundSelectedItems);
                                    const allSelected = itemKeys.every(key => newSelectedItems.has(key));
                                    
                                    itemKeys.forEach(key => {
                                      if (allSelected) {
                                        newSelectedItems.delete(key);
                                      } else {
                                        newSelectedItems.add(key);
                                      }
                                    });
                                    setRefundSelectedItems(newSelectedItems);
                                  } else {
                                  const itemKeysData = remainingIds.map(itemId => ({
                                    item,
                                    variantName: variant.variantName,
                                    itemId: itemId
                                  }));
                                  handleBatchItemSelect(itemKeys, itemKeysData);
                                  }
                                }}
                              size="small"
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                        )}
                        {editMode && !showCheckboxes && (
                          <TableCell>
                            <Checkbox
                              checked={selectedItemsForBulkAssignment.has(item._id)}
                              onChange={() => {
                                const newSelected = new Set(selectedItemsForBulkAssignment);
                                if (newSelected.has(item._id)) {
                                  newSelected.delete(item._id);
                                } else {
                                  newSelected.add(item._id);
                                }
                                setSelectedItemsForBulkAssignment(newSelected);
                              }}
                              size="small"
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                        )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ textTransform: "lowercase" }}> 
                                {item.menuItemId?.name} - {variant.variantName} variant
                              </Typography>
                              <Typography variant="caption" color="error">
                                *
                              </Typography>
                              {shouldExpand && (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleSidesExpansion(variantKey)}
                                  sx={{ p: 0.5 }}
                                >
                                  {expandedSides[variantKey] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {variant.variantName}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                              ${variantPrice} / per variant
                            </Typography>
                          </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2">{remainingQuantity}</Typography>
                            {showCheckboxes && remainingQuantity > 1 && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (refundMode) {
                                    handleRefundItemClick({
                                      menuItemId: item.menuItemId?._id,
                                      itemName: item.menuItemId?.name,
                                      variantName: variant.variantName,
                                      isVariant: true,
                                      allItemIds: remainingIds, // Only show remaining (non-cancelled) items
                                      variant: variant,
                                      item: item
                                    });
                                  } else {
                                    handleBusinessCancelClick({
                                  menuItemId: item.menuItemId?._id,
                                  itemName: item.menuItemId?.name,
                                  variantName: variant.variantName,
                                  isVariant: true,
                                  allItemIds: allItemIds,
                                  variant: variant,
                                  item: item
                                    });
                                  }
                                }}
                                sx={{ 
                                  p: 0.5,
                                  bgcolor: "action.hover",
                                  "&:hover": { bgcolor: "action.selected" }
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {hasItemNotes(item) && (
                                <Tooltip title={getAllNotes(item)} arrow placement="top" PopperProps={{ style: { zIndex: 10001 } }}>
                                  <IconButton size="small" sx={{ p: 0.5 }}>
                                    <NotesIcon fontSize="small" color="primary" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {hasItemCustomizations(item) && (
                                <IconButton size="small" sx={{ p: 0.5 }}>
                                  <CustomizeIcon size="16" color="#6200EE" />
                                </IconButton>
                              )}
                              {!hasItemNotes(item) && !hasItemCustomizations(item) && (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {renderCustomerCell(userOrder, index, item._id)}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    

                    // ====================
                    // ROW 2: PENDING APPROVAL ITEMS (Can be modified) - Skip in refund mode
                    // ====================
                    if (!refundMode && pendingQuantity > 0) {
                      const currentStatus = variant.itemStatus || item.itemStatus || "new items";
                      const backgroundColor = getStatusBackgroundColor(currentStatus);
                      if (pendingQuantity === 1) {
                        // Show individual pending item
                        const itemId = displayPendingIds[0];
                        const itemNum = variant.items?.find(vi => vi._id === itemId)?.itemNumber || 1;
                        rows.push(
                          <TableRow key={`${variantKey}-pending-${itemId}`} sx={{ opacity: 0.6, backgroundColor }}>
                            {showCheckboxes && (
                              <TableCell>
                                <Checkbox
                                  checked={selectedItems.has(`${item.menuItemId?._id}-${variant.variantName}-${itemId}`)}
                                  onChange={() => handleItemSelect(`${item.menuItemId?._id}-${variant.variantName}-${itemId}`, item, variant.variantName, itemId)}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textTransform: "lowercase", textDecoration: "line-through", color: "text.secondary" }}>
                                {item.menuItemId?.name} - {variant.variantName} variant (item #{itemNum} - pending cancellation)
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary", textTransform: "lowercase" }}>
                                ${variantPrice} / per variant
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary" }}>1</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            </TableCell>
                            <TableCell>
                              {renderCustomerCell(userOrder, index, { avatarOpacity: 0.6, textDecoration: "line-through", color: "text.secondary" })}
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        // Show consolidated pending row
                        rows.push(
                          <TableRow key={`${variantKey}-pending-group`} sx={{ opacity: 0.6, backgroundColor }}>
                            {showCheckboxes && (
                              <TableCell>
                                <Checkbox
                                  checked={displayPendingIds.every(itemId => 
                                    selectedItems.has(`${item.menuItemId?._id}-${variant.variantName}-${itemId}`)
                                  )}
                                  onChange={() => {
                                    const itemKeys = displayPendingIds.map(itemId => 
                                      `${item.menuItemId?._id}-${variant.variantName}-${itemId}`
                                    );
                                    const itemKeysData = displayPendingIds.map(itemId => ({
                                      item,
                                      variantName: variant.variantName,
                                      itemId: itemId
                                    }));
                                    handleBatchItemSelect(itemKeys, itemKeysData);
                                  }}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textTransform: "lowercase", textDecoration: "line-through", color: "text.secondary" }}>
                                {item.menuItemId?.name} - {variant.variantName} variant (pending cancellation)
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary", textTransform: "lowercase" }}>
                                ${variantPrice} / per variant
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                                  {pendingQuantity}
                                </Typography>
                                {showCheckboxes && pendingQuantity > 1 && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleBusinessCancelClick({
                                      menuItemId: item.menuItemId?._id,
                                      itemName: item.menuItemId?.name,
                                      variantName: variant.variantName,
                                      isVariant: true,
                                      allItemIds: displayPendingIds,
                                      variant: variant,
                                      item: item
                                    })}
                                    sx={{ 
                                      p: 0.5,
                                      bgcolor: "action.hover",
                                      "&:hover": { bgcolor: "action.selected" }
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            </TableCell>
                            <TableCell>
                              {renderCustomerCell(userOrder, index, { avatarOpacity: 0.6, textDecoration: "line-through", color: "text.secondary" })}
                            </TableCell>
                          </TableRow>
                        );
                      }
                    }

                    // ====================
                    // ROW 3: APPROVED ITEMS (Cannot be modified - Disabled) - Skip in refund mode
                    // ====================
                    if (!refundMode && approvedQuantity > 0) {
                      const currentStatus = variant.itemStatus || item.itemStatus || "cancelled items";
                      const backgroundColor = getStatusBackgroundColor(currentStatus);
                      if (approvedQuantity === 1) {
                        // Show individual approved item
                        const itemId = displayApprovedIds[0];
                        const itemNum = variant.items?.find(vi => vi._id === itemId)?.itemNumber || 1;
                        rows.push(
                          <TableRow key={`${variantKey}-approved-${itemId}`} sx={{ opacity: 0.8, backgroundColor }}>
                            {showCheckboxes && (
                              <TableCell>
                                <Checkbox
                                  checked={true}
                                  disabled={true}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textTransform: "lowercase",color: "text.disabled" }}>
                                <span style={{ textDecoration: "line-through" }}>
                                  {item.menuItemId?.name} - {variant.variantName} variant (item #{itemNum})
                                </span>
                                <span> (cancelled)</span>
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ textDecoration: "line-through", color: "text.disabled" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.disabled", textTransform: "lowercase" }}>
                                ${variantPrice} / per variant
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.disabled" }}>1</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.disabled">-</Typography>
                            </TableCell>
                            <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.4, textDecoration: "line-through", color: "text.disabled" })}
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        // Show consolidated approved row
                        rows.push(
                          <TableRow key={`${variantKey}-approved-group`} sx={{ opacity: 0.8, backgroundColor }}>
                            {showCheckboxes && (
                              <TableCell>
                                <Checkbox
                                  checked={true}
                                  disabled={true}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.disabled" }}>
                                <span style={{ textDecoration: "line-through" }}>
                                  {item.menuItemId?.name} - {variant.variantName} variant
                                </span>
                                <span> (cancelled)</span>
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ textDecoration: "line-through", color: "text.disabled" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.disabled", textTransform: "lowercase" }}>
                                ${variantPrice} / per variant
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.disabled" }}>
                                {approvedQuantity}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.disabled">-</Typography>
                            </TableCell>
                            <TableCell>
                              {renderCustomerCell(userOrder, index, { avatarOpacity: 0.8, textDecoration: "line-through", color: "text.disabled" })}
                            </TableCell>
                          </TableRow>
                        );
                      }
                    }
                    
                    // ====================
                    // ROW: PENDING REFUND ITEMS (Selected but not yet submitted - similar to pending cancellation)
                    // ====================
                    if (refundMode && pendingRefundQuantity > 0) {
                      const currentStatus = variant.itemStatus || item.itemStatus || "new items";
                      const backgroundColor = getStatusBackgroundColor(currentStatus);
                      if (pendingRefundQuantity === 1) {
                        // Show individual pending refund item
                        const itemId = pendingRefundIds[0];
                        const itemNum = variant.items?.find(vi => vi._id === itemId)?.itemNumber || 1;
                        rows.push(
                          <TableRow key={`${variantKey}-pending-refund-${itemId}`} sx={{ opacity: 0.6, backgroundColor }}>
                            {showCheckboxes && (
                              <TableCell>
                                <Checkbox
                                  checked={true}
                                  onChange={() => {
                                    const itemKey = `${item.menuItemId?._id}-${variant.variantName}-${itemId}`;
                                    const newSelectedItems = new Set(refundSelectedItems);
                                    newSelectedItems.delete(itemKey);
                                    setRefundSelectedItems(newSelectedItems);
                                  }}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.secondary" }}>
                                {item.menuItemId?.name} - {variant.variantName} variant (item #{itemNum} - pending refund)
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ color: "text.secondary" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: "text.secondary", textTransform: "lowercase" }}>
                                ${variantPrice} / per variant
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: "text.secondary" }}>1</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            </TableCell>
                            <TableCell>
                              {renderCustomerCell(userOrder, index, { avatarOpacity: 0.6, color: "text.secondary" })}
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        // Show consolidated pending refund row
                        rows.push(
                          <TableRow key={`${variantKey}-pending-refund-group`} sx={{ opacity: 0.6, backgroundColor }}>
                            {showCheckboxes && (
                              <TableCell>
                                <Checkbox
                                  checked={pendingRefundIds.every(itemId => {
                                    const itemKey = `${item.menuItemId?._id}-${variant.variantName}-${itemId}`;
                                    return refundSelectedItems.has(itemKey);
                                  })}
                                  onChange={() => {
                                    const itemKeys = pendingRefundIds.map(itemId => 
                                      `${item.menuItemId?._id}-${variant.variantName}-${itemId}`
                                    );
                                    const newSelectedItems = new Set(refundSelectedItems);
                                    
                                    // If all are selected, uncheck all; otherwise, select all
                                    const allSelected = itemKeys.every(key => newSelectedItems.has(key));
                                    itemKeys.forEach(key => {
                                      if (allSelected) {
                                        newSelectedItems.delete(key);
                                      } else {
                                        newSelectedItems.add(key);
                                      }
                                    });
                                    setRefundSelectedItems(newSelectedItems);
                                  }}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.secondary" }}>
                                {item.menuItemId?.name} - {variant.variantName} variant (pending refund)
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ color: "text.secondary" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: "text.secondary", textTransform: "lowercase" }}>
                                ${variantPrice} / per variant
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                  {pendingRefundQuantity}
                                </Typography>
                                {showCheckboxes && pendingRefundQuantity > 1 && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRefundItemClick({
                                      menuItemId: item.menuItemId?._id,
                                      itemName: item.menuItemId?.name,
                                      variantName: variant.variantName,
                                      isVariant: true,
                                      allItemIds: pendingRefundIds, // Only show pending refund items
                                      variant: variant,
                                      item: item
                                    })}
                                    sx={{ 
                                      p: 0.5,
                                      bgcolor: "action.hover",
                                      "&:hover": { bgcolor: "action.selected" }
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            </TableCell>
                            <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.6, color: "text.secondary" })}
                            </TableCell>
                          </TableRow>
                        );
                      }
                    }
                    
                    // ====================
                    // ROW: CONFIRMED REFUNDED ITEMS (Already refunded - readonly, disabled, no strikethrough)
                    // ====================
                    if (refundMode && confirmedRefundedQuantity > 0) {
                      const refundedQuantity = confirmedRefundedQuantity;
                      const currentStatus = variant.itemStatus || item.itemStatus || "new items";
                      const backgroundColor = getStatusBackgroundColor(currentStatus);
                      
                      rows.push(
                        <TableRow key={`${variantKey}-refunded`} sx={{ opacity: 0.8, backgroundColor }}>
                          {showCheckboxes && (
                            <TableCell>
                              <Checkbox
                                checked={true}
                                disabled={true}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.disabled" }}>
                              {item.menuItemId?.name} - {variant.variantName} variant - (refunded)
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography sx={{ color: "text.disabled" }}>
                              {variant.variantName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: "text.disabled", textTransform: "lowercase" }}>
                              ${variantPrice} / per variant
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: "text.disabled" }}>
                              {refundedQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.disabled">-</Typography>
                          </TableCell>
                          <TableCell>
                            {renderCustomerCell(userOrder, index, { avatarOpacity: 0.4, color: "text.disabled" })}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    // ====================
                    // ROW: REFUNDED ITEMS (In normal view - disabled, no strikethrough)
                    // ====================
                    if (!refundMode && !showCheckboxes && refundedQuantity > 0) {
                      const currentStatus = variant.itemStatus || item.itemStatus || "new items";
                      const backgroundColor = getStatusBackgroundColor(currentStatus);
                      if (refundedQuantity === 1) {
                        // Show individual refunded item
                        const itemId = refundedIds[0];
                        const itemNum = variant.items?.find(vi => vi._id === itemId)?.itemNumber || 1;
                        rows.push(
                          <TableRow
                            key={`${variantKey}-refunded-normal-${itemId}`}
                            sx={{ opacity: 0.8, backgroundColor }}
                          >
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code ||
                                `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  textTransform: "lowercase",
                                  color: "text.disabled",
                                }}
                              >
                                <span
                                  style={{ textDecoration: "line-through" }}
                                >
                                  {item.menuItemId?.name} -{" "}
                                  {variant.variantName} variant (item #{itemNum}
                                  )
                                </span>
                                <span> - refunded</span>
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ color: "text.disabled" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.disabled",
                                  textTransform: "lowercase",
                                }}
                              >
                                ${variantPrice} / per variant
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ color: "text.disabled" }}
                              >
                                1
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                -
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  src={`${IMAGE_URL}${
                                    userOrder.userProfilePic || ""
                                  }`}
                                  sx={{ width: 24, height: 24, opacity: 0.4 }}
                                >
                                  {userOrder.userFullName?.charAt(0) || "U"}
                                </Avatar>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textTransform: "lowercase",
                                    color: "text.disabled",
                                  }}
                                >
                                  {userOrder.userFullName || "user"}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        // Show consolidated refunded row
                        rows.push(
                          <TableRow
                            key={`${variantKey}-refunded-normal-group`}
                            sx={{ opacity: 0.8, backgroundColor }}
                          >
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code ||
                                `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  textTransform: "lowercase",
                                  color: "text.disabled",
                                }}
                              >
                                <span
                                  style={{ textDecoration: "line-through" }}
                                >
                                  {item.menuItemId?.name} -{" "}
                                  {variant.variantName} variant
                                </span>
                                <span> - (refunded)</span>
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ color: "text.disabled" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.disabled",
                                  textTransform: "lowercase",
                                }}
                              >
                                ${variantPrice} / per variant
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ color: "text.disabled" }}
                              >
                                {refundedQuantity}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                -
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  src={`${IMAGE_URL}${
                                    userOrder.userProfilePic || ""
                                  }`}
                                  sx={{ width: 24, height: 24, opacity: 0.4 }}
                                >
                                  {userOrder.userFullName?.charAt(0) || "U"}
                                </Avatar>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textTransform: "lowercase",
                                    color: "text.disabled",
                                  }}
                                >
                                  {userOrder.userFullName || "user"}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    }
                    
                    // Render sub-items if expanded (only for remaining items)
                    if (shouldExpand && expandedSides[variantKey] && remainingQuantity > 0) {
                      rows.push(...renderVariantSubItems(variant, variantKey, [...displayPendingIds, ...displayApprovedIds, ...refundedIds]));
                    }
                    
                    return rows;
                  }).filter(Boolean).flat();
                } else {
                  // For regular items
                  // Get all item ids
                  const allItemIds = item.items?.map(subItem => subItem._id) || [];
                  const totalQuantity = allItemIds.length;
                  
                  // Get cancelled items from API data
                  const cancelledData = getCancelledItemIds(item);
                  const pendingItemIds = cancelledData.pending || [];
                  const approvedItemIds = cancelledData.approved || [];
                  
                  // Get refunded items from API data (from refunds array)
                  const apiRefundedIds = getRefundedItemIds(item, null);
                  
                  // In refund mode, filter out cancelled items completely
                  const activeSelectedItems = refundMode ? refundSelectedItems : selectedItems;
                  
                  // Determine display logic based on showCheckboxes state and mode
                  let displayPendingIds, displayApprovedIds, remainingIds, refundedIds, pendingRefundIds;
                  
                  if (refundMode) {
                    // Refund mode: Show only paid items (excluding cancelled approved)
                    // Get already refunded items (refundRequest === true OR from refunds array)
                    refundedIds = allItemIds.filter(itemId => 
                      isItemRefunded(item, null, itemId)
                    );
                    
                    // Get pending refund items (selected in refundSelectedItems but not yet refunded)
                    pendingRefundIds = allItemIds.filter(itemId => {
                      const itemKey = `${item.menuItemId?._id}-${itemId}`;
                      return refundSelectedItems.has(itemKey) && !refundedIds.includes(itemId) && !approvedItemIds.includes(itemId);
                    });
                    
                    // Remaining = all items minus (cancelled + pending refund + confirmed refunded)
                    remainingIds = allItemIds.filter(itemId => 
                      !approvedItemIds.includes(itemId) && 
                      !pendingRefundIds.includes(itemId) &&
                      !refundedIds.includes(itemId)
                    );
                    displayPendingIds = [];
                    displayApprovedIds = [];
                  } else if (showCheckboxes) {
                    // Cancellation mode with checkboxes: use selectedItems for pending
                    displayPendingIds = allItemIds.filter(itemId => 
                      selectedItems.has(`${item.menuItemId?._id}-${itemId}`) &&
                      !approvedItemIds.includes(itemId)
                    );
                    displayApprovedIds = approvedItemIds;
                    
                    const allCancelledIds = [...displayPendingIds, ...displayApprovedIds];
                    remainingIds = allItemIds.filter(itemId => 
                      !allCancelledIds.includes(itemId)
                    );
                      refundedIds = [];
                      pendingRefundIds = [];
                    } else {
                      // Normal mode: show API data as-is
                    displayPendingIds = pendingItemIds;
                    displayApprovedIds = approvedItemIds;
                    
                    // Get refunded items from API (from refunds array)
                    refundedIds = apiRefundedIds;
                    
                    const allCancelledIds = [...pendingItemIds, ...approvedItemIds];
                    const allExcludedIds = [...allCancelledIds, ...refundedIds];
                    remainingIds = allItemIds.filter(itemId => 
                      !allExcludedIds.includes(itemId)
                    );
                      pendingRefundIds = [];
                  }
                  
                  const remainingQuantity = remainingIds.length;
                  const pendingQuantity = displayPendingIds.length;
                  const approvedQuantity = displayApprovedIds.length;
                  const refundedQuantity = refundedIds.length;
                  const pendingRefundQuantity = refundMode ? pendingRefundIds.length : 0;
                  const confirmedRefundedQuantity = refundMode ? refundedIds.length : 0;
                  
                 // Show cancelled items in normal view (do not filter entire row out)
                  
                  const rows = [];
                  
                   // ====================
                   // ROW 1: REMAINING ITEMS (No cancellation, no refund)
                   // ====================
                   // In normal view, still render remaining items; cancelled/refunded get their own rows
                    
                    if (remainingQuantity > 0) {
                      const currentStatus = item.itemStatus || "new items";
                      const backgroundColor = getStatusBackgroundColor(currentStatus);
                    
                    rows.push(
                      <TableRow key={item.menuItemId?._id} sx={{ backgroundColor }}>
                        {showCheckboxes && (
                          <TableCell>
                            <Checkbox
                              checked={remainingIds.some(itemId => {
                                const itemKey = `${item.menuItemId?._id}-${itemId}`;
                                return refundMode 
                                  ? refundSelectedItems.has(itemKey)
                                  : selectedItems.has(itemKey);
                              })}
                              onChange={() => {
                                const itemKeys = remainingIds.map(itemId => 
                                  `${item.menuItemId?._id}-${itemId}`
                                );
                                
                                if (refundMode) {
                                  const newSelectedItems = new Set(refundSelectedItems);
                                  const allSelected = itemKeys.every(key => newSelectedItems.has(key));
                                  
                                  itemKeys.forEach(key => {
                                    if (allSelected) {
                                      newSelectedItems.delete(key);
                                    } else {
                                      newSelectedItems.add(key);
                                    }
                                  });
                                  setRefundSelectedItems(newSelectedItems);
                                } else {
                                const itemKeysData = remainingIds.map(itemId => ({
                                  item,
                                  variantName: null,
                                  itemId: itemId
                                }));
                                handleBatchItemSelect(itemKeys, itemKeysData);
                                }
                              }}
                              size="small"
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                        )}
                        {editMode && !showCheckboxes && (
                          <TableCell>
                            <Checkbox
                              checked={selectedItemsForBulkAssignment.has(item._id)}
                              onChange={() => {
                                const newSelected = new Set(selectedItemsForBulkAssignment);
                                if (newSelected.has(item._id)) {
                                  newSelected.delete(item._id);
                                } else {
                                  newSelected.add(item._id);
                                }
                                setSelectedItemsForBulkAssignment(newSelected);
                              }}
                              size="small"
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{ textTransform: "lowercase" }}>
                          {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.name}
                            </Typography>
                            <Typography variant="caption" color="error">
                              *
                            </Typography>
                            {shouldExpand && (
                              <IconButton
                                size="small"
                                onClick={() => toggleSidesExpansion(itemKey)}
                                sx={{ p: 0.5 }}
                              >
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textTransform: "lowercase" }}>standard</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                            ${item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0} / per item
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2">{remainingQuantity}</Typography>
                            {showCheckboxes && remainingQuantity > 1 && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (refundMode) {
                                    handleRefundItemClick({
                                      menuItemId: item.menuItemId?._id,
                                      itemName: item.menuItemId?.name,
                                      variantName: null,
                                      isVariant: false,
                                      allItemIds: remainingIds, // Only show remaining (non-cancelled) items
                                      variant: null,
                                      item: item
                                    });
                                  } else {
                                    handleBusinessCancelClick({
                                  menuItemId: item.menuItemId?._id,
                                  itemName: item.menuItemId?.name,
                                  variantName: null,
                                  isVariant: false,
                                  allItemIds: allItemIds,
                                  variant: null,
                                  item: item
                                    });
                                  }
                                }}
                                sx={{ 
                                  p: 0.5,
                                  bgcolor: "action.hover",
                                  "&:hover": { bgcolor: "action.selected" }
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {hasItemNotes(item) && (
                              <Tooltip title={getAllNotes(item)} arrow placement="top" PopperProps={{ style: { zIndex: 10001 } }}>
                                <IconButton size="small" sx={{ p: 0.5 }}>
                                  <NotesIcon fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasItemCustomizations(item) && (
                              <IconButton size="small" sx={{ p: 0.5 }}>
                                <CustomizeIcon size="16" color="#6200EE" />
                              </IconButton>
                            )}
                            {!hasItemNotes(item) && !hasItemCustomizations(item) && (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {renderCustomerCell(userOrder, index, item._id)}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  

                  // ====================
                  // ROW 2: PENDING APPROVAL ITEMS (Can be modified) - Skip in refund mode
                  // ====================
                  if (!refundMode && pendingQuantity > 0) {
                    const currentStatus = item.itemStatus || "new items";
                    const backgroundColor = getStatusBackgroundColor(currentStatus);
                    if (pendingQuantity === 1) {
                      // Show individual pending item
                      const itemId = displayPendingIds[0];
                      const itemNum = item.items?.find(si => si._id === itemId)?.itemNumber || 1;
                      rows.push(
                        <TableRow key={`${item.menuItemId?._id}-pending-${itemId}`} sx={{ opacity: 0.8, backgroundColor }}>
                          {showCheckboxes && (
                            <TableCell>
                              <Checkbox
                                checked={selectedItems.has(`${item.menuItemId?._id}-${itemId}`)}
                                onChange={() => handleItemSelect(`${item.menuItemId?._id}-${itemId}`, item, null, itemId)}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: "lowercase", textDecoration: "line-through", color: "text.secondary" }}>
                              {item.menuItemId?.name} (item #{itemNum} - pending cancellation)
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography sx={{ textDecoration: "line-through", color: "text.secondary" }}>standard</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary", textTransform: "lowercase" }}>
                              ${item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0} / per item
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary" }}>1</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          </TableCell>
                          <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.8, textDecoration: "line-through", color: "text.secondary" })}
                          </TableCell>
                        </TableRow>
                      );
                    } else {
                      // Show consolidated pending row
                      rows.push(
                        <TableRow key={`${item.menuItemId?._id}-pending-group`} sx={{ opacity: 0.8, backgroundColor }}>
                          {showCheckboxes && (
                            <TableCell>
                              <Checkbox
                                checked={displayPendingIds.every(itemId => 
                                  selectedItems.has(`${item.menuItemId?._id}-${itemId}`)
                                )}
                                onChange={() => {
                                  const itemKeys = displayPendingIds.map(itemId => 
                                    `${item.menuItemId?._id}-${itemId}`
                                  );
                                  const itemKeysData = displayPendingIds.map(itemId => ({
                                    item,
                                    variantName: null,
                                    itemId: itemId
                                  }));
                                  handleBatchItemSelect(itemKeys, itemKeysData);
                                }}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: "lowercase", textDecoration: "line-through", color: "text.secondary" }}>
                              {item.menuItemId?.name} (pending cancellation)
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography sx={{ textDecoration: "line-through", color: "text.secondary" }}>standard</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary", textTransform: "lowercase" }}>
                              ${item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0} / per item
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                                {pendingQuantity}
                              </Typography>
                              {showCheckboxes && pendingQuantity > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleBusinessCancelClick({
                                    menuItemId: item.menuItemId?._id,
                                    itemName: item.menuItemId?.name,
                                    variantName: null,
                                    isVariant: false,
                                    allItemIds: displayPendingIds,
                                    variant: null,
                                    item: item
                                  })}
                                  sx={{ 
                                    p: 0.5,
                                    bgcolor: "action.hover",
                                    "&:hover": { bgcolor: "action.selected" }
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          </TableCell>
                          <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.8, textDecoration: "line-through", color: "text.secondary" })}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  }

                  // ====================
                  // ROW 3: APPROVED ITEMS (Cannot be modified - Disabled) - Skip in refund mode
                  // ====================
                  if (!refundMode && approvedQuantity > 0) {
                    const currentStatus = item.itemStatus || "cancelled items";
                    const backgroundColor = getStatusBackgroundColor(currentStatus);
                    if (approvedQuantity === 1) {
                      // Show individual approved item
                      const itemId = displayApprovedIds[0];
                      const itemNum = item.items?.find(si => si._id === itemId)?.itemNumber || 1;
                      rows.push(
                        <TableRow key={`${item.menuItemId?._id}-approved-${itemId}`} sx={{ opacity: 0.8, backgroundColor }}>
                          {showCheckboxes && (
                            <TableCell>
                              <Checkbox
                                checked={true}
                                disabled={true}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.disabled" }}>
                              <span style={{ textDecoration: "line-through" }}>
                                {item.menuItemId?.name} (item #{itemNum})
                              </span>
                              <span> - cancelled</span>
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography sx={{ textDecoration: "line-through", color: "text.disabled" }}>standard</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.disabled", textTransform: "lowercase" }}>
                              ${item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0} / per item
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.disabled" }}>1</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.disabled">-</Typography>
                          </TableCell>
                          <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.8, textDecoration: "line-through", color: "text.disabled" })}
                          </TableCell>
                        </TableRow>
                      );
                    } else {
                      // Show consolidated approved row
                      rows.push(
                        <TableRow key={`${item.menuItemId?._id}-approved-group`} sx={{ opacity: 0.8, backgroundColor }}>
                          {showCheckboxes && (
                            <TableCell>
                              <Checkbox
                                checked={true}
                                disabled={true}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.disabled" }}>
                              <span style={{ textDecoration: "line-through" }}>
                                {item.menuItemId?.name}
                              </span>
                              <span> (cancelled)</span>
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography sx={{ textDecoration: "line-through", color: "text.disabled" }}>standard</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.disabled", textTransform: "lowercase" }}>
                              ${item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0} / per item
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.disabled" }}>
                              {approvedQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.disabled">-</Typography>
                          </TableCell>
                          <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.8, textDecoration: "line-through", color: "text.disabled" })}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  }

                  // ====================
                  // ROW: PENDING REFUND ITEMS (Selected but not yet submitted - Non-variant)
                  // ====================
                  if (refundMode && pendingRefundQuantity > 0) {
                    const currentStatus = item.itemStatus || "new items";
                    const backgroundColor = getStatusBackgroundColor(currentStatus);
                    if (pendingRefundQuantity === 1) {
                      // Show individual pending refund item
                      const itemId = pendingRefundIds[0];
                      const itemNum = item.items?.find(si => si._id === itemId)?.itemNumber || 1;
                      rows.push(
                        <TableRow key={`${item.menuItemId?._id}-pending-refund-${itemId}`} sx={{ opacity: 0.6, backgroundColor }}>
                          {showCheckboxes && (
                            <TableCell>
                              <Checkbox
                                checked={true}
                                onChange={() => {
                                  const itemKey = `${item.menuItemId?._id}-${itemId}`;
                                  const newSelectedItems = new Set(refundSelectedItems);
                                  newSelectedItems.delete(itemKey);
                                  setRefundSelectedItems(newSelectedItems);
                                }}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.secondary" }}>
                              {item.menuItemId?.name} (item #{itemNum} - pending refund)
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography sx={{ color: "text.secondary" }}>standard</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: "text.secondary", textTransform: "lowercase" }}>
                              ${item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0} / per item
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>1</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          </TableCell>
                          <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.6, color: "text.secondary" })}
                          </TableCell>
                        </TableRow>
                      );
                    } else {
                      // Show consolidated pending refund row
                      rows.push(
                        <TableRow key={`${item.menuItemId?._id}-pending-refund-group`} sx={{ opacity: 0.6, backgroundColor }}>
                          {showCheckboxes && (
                            <TableCell>
                              <Checkbox
                                checked={pendingRefundIds.every(itemId => {
                                  const itemKey = `${item.menuItemId?._id}-${itemId}`;
                                  return refundSelectedItems.has(itemKey);
                                })}
                                onChange={() => {
                                  const itemKeys = pendingRefundIds.map(itemId => 
                                    `${item.menuItemId?._id}-${itemId}`
                                  );
                                  const newSelectedItems = new Set(refundSelectedItems);
                                  
                                  // If all are selected, uncheck all; otherwise, select all
                                  const allSelected = itemKeys.every(key => newSelectedItems.has(key));
                                  itemKeys.forEach(key => {
                                    if (allSelected) {
                                      newSelectedItems.delete(key);
                                    } else {
                                      newSelectedItems.add(key);
                                    }
                                  });
                                  setRefundSelectedItems(newSelectedItems);
                                }}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.secondary" }}>
                              {item.menuItemId?.name} (pending refund)
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography sx={{ color: "text.secondary" }}>standard</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: "text.secondary", textTransform: "lowercase" }}>
                              ${item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0} / per item
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {pendingRefundQuantity}
                              </Typography>
                              {showCheckboxes && pendingRefundQuantity > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleRefundItemClick({
                                    menuItemId: item.menuItemId?._id,
                                    itemName: item.menuItemId?.name,
                                    variantName: null,
                                    isVariant: false,
                                    allItemIds: pendingRefundIds, // Only show pending refund items
                                    variant: null,
                                    item: item
                                  })}
                                  sx={{ 
                                    p: 0.5,
                                    bgcolor: "action.hover",
                                    "&:hover": { bgcolor: "action.selected" }
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          </TableCell>
                          <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.6, color: "text.secondary" })}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  }

                  // ====================
                  // ROW: CONFIRMED REFUNDED ITEMS (Already refunded - Non-variant, disabled, no strikethrough)
                  // ====================
                  if (refundMode && confirmedRefundedQuantity > 0) {
                    const refundedQuantity = confirmedRefundedQuantity;
                    const currentStatus = item.itemStatus || "new items";
                    const backgroundColor = getStatusBackgroundColor(currentStatus);
                    
                    rows.push(
                      <TableRow key={`${item.menuItemId?._id}-refunded`} sx={{ opacity: 0.8, backgroundColor }}>
                        {showCheckboxes && (
                          <TableCell>
                            <Checkbox
                              checked={true}
                              disabled={true}
                              size="small"
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{ textTransform: "lowercase" }}>
                          {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.disabled" }}>
                            {item.menuItemId?.name} - (refunded)
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textTransform: "lowercase" }}>
                          <Typography sx={{ color: "text.disabled" }}>standard</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "text.disabled", textTransform: "lowercase" }}>
                            ${item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0} / per item
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "text.disabled" }}>
                            {refundedQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.disabled">-</Typography>
                        </TableCell>
                        <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.4, color: "text.disabled" })}
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // ====================
                  // ROW: REFUNDED ITEMS (In normal view - Non-variant, disabled, no strikethrough)
                  // ====================
                  if (!refundMode && !showCheckboxes && refundedQuantity > 0) {
                    const currentStatus = item.itemStatus || "new items";
                    const backgroundColor = getStatusBackgroundColor(currentStatus);
                    if (refundedQuantity === 1) {
                      // Show individual refunded item
                      const itemId = refundedIds[0];
                      const itemNum = item.items?.find(si => si._id === itemId)?.itemNumber || 1;
                      rows.push(
                        <TableRow
                          key={`${item.menuItemId?._id}-refunded-normal-${itemId}`}
                          sx={{ opacity: 0.8, backgroundColor }}
                        >
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code ||
                              `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                textTransform: "lowercase",
                                color: "text.disabled",
                              }}
                            >
                              <span style={{ textDecoration: "line-through" }}>
                                {item.menuItemId?.name} (item #{itemNum})
                              </span>
                              <span> - (refunded)</span>
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography sx={{ color: "text.disabled" }}>
                              standard
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.disabled",
                                textTransform: "lowercase",
                              }}
                            >
                              ${item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0} / per item
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.disabled" }}
                            >
                              1
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.disabled">
                              -
                            </Typography>
                          </TableCell>
                          <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.4, color: "text.disabled" })}
                          </TableCell>
                        </TableRow>
                      );
                    } else {
                      // Show consolidated refunded row
                      rows.push(
                        <TableRow
                          key={`${item.menuItemId?._id}-refunded-normal-group`}
                          sx={{ opacity: 0.8, backgroundColor }}
                        >
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code ||
                              `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                textTransform: "lowercase",
                                color: "text.disabled",
                              }}
                            >
                              <span style={{ textDecoration: "line-through" }}>
                                {item.menuItemId?.name}
                              </span>
                              <span> - (refunded)</span>
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography sx={{ color: "text.disabled" }}>
                              standard
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.disabled",
                                textTransform: "lowercase",
                              }}
                            >
                              ${item.itemPrice && item.quantity ? (item.itemPrice / item.quantity) : 0} / per item
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.disabled" }}
                            >
                              {refundedQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.disabled">
                              -
                            </Typography>
                          </TableCell>
                          <TableCell>
{renderCustomerCell(userOrder, index, { avatarOpacity: 0.4, color: "text.disabled" })}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  }

                  // Render item details with sides if expanded (only for remaining items)
                  if (shouldExpand && isExpanded && remainingQuantity > 0) {
                    rows.push(...renderSubItems(item, `${userIndex}-${index}`, [...displayPendingIds, ...displayApprovedIds, ...refundedIds]));
                  }
                  
                  return (
                    <React.Fragment key={itemKey}>
                      {rows}
                    </React.Fragment>
                  );
                }
              });
            })}
          </TableBody>
        </Table>
      </ColoredTable>
    );
  };


  // Handle dialog close
  const handleClose = () => {
    // Reset states when closing
    setShowCheckboxes(false);
    setSelectedItems(new Set());
    setRefundMode(false);
    setRefundSelectedItems(new Set());
    setSelectedUserId(null);
    autoActivatedRef.current = false; // Reset auto-activation flag
    onClose();
  };

  if (!open) return null;

  return (
    <>
    <CustomOverlay>
      <CustomDialog
        ref={dialogRef}
        style={{ left: dialogPosition.x, top: dialogPosition.y }}
      >
        <DialogHeader onMouseDown={handleDragStart}>
          <DragHandle />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.25rem" },
                textTransform: "lowercase",
                ml: { xs: 1, sm: 2 },
              }}
            >
             {messageType === "order_cancellation_request" ? "cancellation details" : "order details"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              sx={{
                color: "#7C3AED",
                backgroundColor: "#f0f4ff",
                "&:hover": {
                  backgroundColor: "#7C3AED",
                  color: "white",
                },
              }}
            >
              <ChatIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleOptionsMenuOpen}
              size="small"
              sx={{
                color: "#7C3AED",
                backgroundColor: "#f0f4ff",
                "&:hover": {
                  backgroundColor: "#7C3AED",
                  color: "white",
                },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: "#7C3AED",
                backgroundColor: "#f0f4ff",
                "&:hover": {
                  backgroundColor: "#7C3AED",
                  color: "white",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogHeader>

        <ContentContainer>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress size={60} sx={{ color: "#7C3AED" }} />
            </Box>
          ) : !orderData ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#64748B",
              }}
            >
              <Typography variant="h6" sx={{ textTransform: "lowercase" }}>
                no order data available
              </Typography>
            </Box>
          ) : (
            <ScrollableContainer>
              {/* Business Info Header */}
              <BusinessHeader>
                <BusinessInfo>
                  <BusinessLogo
                    src={`${MEDIA_URL}${
                      orderData.locationId?.locationProfilePhoto || ""
                    }`}
                  >
                    {orderData.businessId?.businessName?.charAt(0) || "B"}
                  </BusinessLogo>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        textTransform: "lowercase",
                        fontSize: { xs: "1rem", md: "1.2rem" },
                      }}
                    >
                      {orderData.businessId?.businessName || "business name"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "lowercase" }}
                    >
                      {orderData.locationId?.businessLocationName || "location"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", textTransform: "lowercase" }}
                    >
                      tax invoice
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", textTransform: "lowercase" }}
                    >
                      {orderData.locationId?.emailAddress || "business@email.com"}
                    </Typography>
                  </Box>
                </BusinessInfo>

                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: "0.7rem", md: "1rem" },
                      display: "block",
                      textTransform: "lowercase",
                    }}
                  >
                    vat#: {orderData.locationId?.businessVatNumber || "aiu89790piaf"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: "0.7rem", md: "1rem" },
                      display: "block",
                      textTransform: "lowercase",
                    }}
                  >
                    reg#: {orderData.locationId?.businessRegistrationNumber || "2017/iso2009-23"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: "0.7rem", md: "1rem" },
                      display: "block",
                      textTransform: "lowercase",
                    }}
                  >
                    tel: {orderData.locationId?.phoneNumber?.number || "020-123195923"}
                  </Typography>
                </Box>
              </BusinessHeader>

              {/* User Card */}
              <UserCard>
                <UserInfo>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      src={`${IMAGE_URL}${orderData.userId?.image?.profilePic || ""}`}
                      sx={{
                        width: { xs: 40, md: 60 },
                        height: { xs: 40, md: 60 },
                      }}
                    >
                      {orderData.userId?.fullName?.charAt(0) || "U"}
                    </Avatar>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, textTransform: "lowercase" }}
                        >
                          order by {orderData.userId?.fullName || "user name"}
                        </Typography>
                        {editMode && (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              ref={selectRef}
                              value={selectedUserId || ""}
                              onChange={handleUserChange}
                              renderValue={(selected) => {
                                if (!selected) return "";
                                const members = getAllGroupMembersForDropdown();
                                const selectedMember = members.find(m => String(m.userId) === String(selected));
                                return selectedMember ? selectedMember.fullName.toLowerCase() : "";
                              }}
                              displayEmpty
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                textTransform: 'lowercase',
                                backgroundColor: '#f0f4ff',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#7C3AED',
                                  borderWidth: '1px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#7C3AED',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#7C3AED',
                                },
                                '& .MuiSelect-select': {
                                  padding: '4px 8px',
                                  fontSize: '0.7rem',
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: 200,
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#FFFFFF',
                                    mt: 0.5,
                                    zIndex: '20000 !important',
                                    '& .MuiMenuItem-root': {
                                      textTransform: 'lowercase',
                                      fontSize: '0.75rem',
                                      color: '#1F2937',
                                      minHeight: 32,
                                      padding: '6px 12px',
                                      '&:hover': {
                                        backgroundColor: '#f0f4ff',
                                      },
                                      '&.Mui-selected': {
                                        backgroundColor: '#f0f4ff',
                                        '&:hover': {
                                          backgroundColor: '#f0f4ff',
                                        },
                                      },
                                    },
                                  },
                                  style: {
                                    zIndex: 20000,
                                  },
                                },
                                PopoverClasses: {
                                  root: 'group-members-popover',
                                },
                                anchorOrigin: {
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                },
                                transformOrigin: {
                                  vertical: 'top',
                                  horizontal: 'left',
                                },
                                disablePortal: false,
                                container: typeof document !== 'undefined' ? document.body : null,
                              }}
                            >
                              {getAllGroupMembersForDropdown().map((member, index) => (
                                <MenuItem key={member.userId || index} value={String(member.userId)}>
                                  {member.fullName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: "lowercase" }}
                      >
                        {orderData.orderMode === "seat_down" ? "seat down" : "collect"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ textTransform: "lowercase" }}
                      >
                        today at {moment(orderData.createdAt).format("h:mm a")}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <AccessTimeIcon sx={{ fontSize: "0.9rem" }} />
                        <Typography variant="caption">
                          {getTimeAgo(orderData.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, textTransform: "lowercase" }}
                    >
                      order id: #{orderData.orderNumber || "248936500"}
                    </Typography>
                    {editMode ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ textTransform: "lowercase" }}
                        >
                          table number:
                        </Typography>
                        <TextField
                          value={tableNumber}
                          onChange={(e) => setTableNumber(e.target.value)}
                          size="small"
                          sx={{
                            width: 60,
                            "& .MuiOutlinedInput-root": {
                              height: 24,
                              fontSize: "0.75rem",
                              backgroundColor: "white",
                              "& fieldset": {
                                borderColor: "#7C3AED",
                              },
                              "&:hover fieldset": {
                                borderColor: "#7C3AED",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#7C3AED",
                              },
                            },
                            "& .MuiInputBase-input": {
                              padding: "4px 8px",
                              textTransform: "lowercase",
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", textTransform: "lowercase" }}
                      >
                        table number: {tableNumber || ""}
                      </Typography>
                    )}
                  
                    {orderData.groupOrder?.userOrders &&
                      orderData.groupOrder.userOrders.length > 1 && (
                        <Typography
                        variant="caption"
                        sx={{ display: "block", textTransform: "lowercase" }}
                        >
                          pax: {orderData.groupOrder.userOrders.length}
                        </Typography>
                      )}
                  </Box>
                </UserInfo>
              </UserCard>

              {/* Search Field with Delete Icon */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <TextField
                    fullWidth
                    size="small" 
                    placeholder="search items..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton 
                            size="small" 
                            onClick={handleClearSearch}
                            sx={{ p: 0.5 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        "& fieldset": {
                          borderColor: "#e5e7eb",
                        },
                        "&:hover fieldset": {
                          borderColor: "#7C3AED",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#7C3AED",
                        },
                      },
                      "& .MuiInputBase-input": {
                        textTransform: "lowercase",
                        "&::placeholder": {
                          textTransform: "lowercase",
                          opacity: 0.8,
                        },
                      },
                    }}
                  />
                  <Tooltip
                    title="delete icon placeholder"
                    arrow
                  >
                    <IconButton
                      onClick={handleDeleteIconClick}
                      sx={{
                        color: '#6200EE',
                        backgroundColor: '#F2E9FF',
                        border: `2px solid #e5e7eb`,
                        "&:hover": {
                          backgroundColor: "#f3f4f6",
                          color: "#374151",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Tabs */}
              <CustomTabs value={activeTab} onChange={handleTabChange}>
                <Tab label="order details" />
                <Tab label="billing details" />
              </CustomTabs>

              {/* Tab Content */}
              <Box>
                {renderOrderItems()}

                {/* Bulk Customer Assignment - Show when items are selected in edit mode */}
                {editMode && selectedItemsForBulkAssignment.size > 0 && (
                  <Box sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 2, 
                    mt: 2, 
                    p: 2, 
                    backgroundColor: "#f0f4ff",
                    borderRadius: 1,
                    border: "1px solid #7C3AED"
                  }}>
                    <Typography variant="body2" sx={{ textTransform: "lowercase", fontWeight: 600 }}>
                      assign {selectedItemsForBulkAssignment.size} item{selectedItemsForBulkAssignment.size > 1 ? 's' : ''} to:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value=""
                        onChange={(e) => {
                          const newUserId = e.target.value;
                          if (newUserId) {
                            handleBulkCustomerAssignment(newUserId);
                          }
                        }}
                        displayEmpty
                        sx={{
                          height: 36,
                          fontSize: '0.875rem',
                          textTransform: 'lowercase',
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#7C3AED',
                            borderWidth: '2px',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#7C3AED',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#7C3AED',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e5e7eb',
                              backgroundColor: '#FFFFFF',
                              mt: 0.5,
                              zIndex: '20000 !important',
                              '& .MuiMenuItem-root': {
                                textTransform: 'lowercase',
                                fontSize: '0.875rem',
                                color: '#1F2937',
                                minHeight: 40,
                                padding: '8px 16px',
                                '&:hover': {
                                  backgroundColor: '#f0f4ff',
                                },
                              },
                            },
                          },
                          style: {
                            zIndex: 20000,
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          select customer
                        </MenuItem>
                        {getAllGroupMembersForDropdown().map((member, index) => (
                          <MenuItem key={member.userId || index} value={String(member.userId)}>
                            {member.fullName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={() => setSelectedItemsForBulkAssignment(new Set())}
                      size="small"
                      sx={{
                        color: "#7C3AED",
                        "&:hover": {
                          backgroundColor: "#e0d4f7",
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                {/* Billing Summary */}
                {renderBillingSummary()}

                {/* Action Buttons - Show when there are selected items */}
                {!refundMode && showCheckboxes && hasSelectedItems() && (
                  <Box sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    gap: 2, 
                    pt: 2, 
                    borderTop: "1px solid #E0E0E0" 
                  }}>
                    {messageType === "order_cancellation_request" && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleReject}
                        sx={{ textTransform: "lowercase" }}
                      >
                        reject
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCancel}
                      sx={{ textTransform: "lowercase" }}
                    >
                      cancel item
                    </Button>
                  </Box>
                )}

                {/* Refund Action Buttons - Show when in refund mode */}
                {refundMode && showCheckboxes && refundSelectedItems.size > 0 && (
                  <Box sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    gap: 2, 
                    mt: 2, 
                    pt: 2, 
                    borderTop: "1px solid #E0E0E0" 
                  }}>
                    <Box sx={{ 
                      display: "flex", 
                      flexDirection: "column", 
                      alignItems: "center",
                      gap: 1,
                      mb: 1
                    }}>
                      <Typography variant="body2" sx={{ textTransform: "lowercase", fontWeight: 600 }}>
                        total refund amount: ${calculateRefundAmount(refundSelectedItems).toFixed(2)}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleRefundDialogClick}
                      sx={{ 
                        textTransform: "lowercase",
                        backgroundColor: "#0085FF",
                        "&:hover": {
                          backgroundColor: "#0073E6"
                        }
                      }}
                    >
                      refund selected
                    </Button>
                  </Box>
                )}

                {/* Order Status Section */}
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f8f9fa",
                    borderRadius: 1,
                    mt: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ textTransform: "lowercase" }}
                    >
                      order status:
                    </Typography>
                    <Chip
                      label={orderData.orderStatus}
                      size="small"
                      sx={{ textTransform: "lowercase", color: "#6200EE" }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ textTransform: "lowercase" }}
                    >
                      payment status:
                    </Typography>
                    <Chip
                      label={orderData.paymentStatus || "pending"}
                      size="small"
                      color={
                        orderData.paymentStatus === "paid"
                          ? "success"
                          : "warning"
                      }
                      sx={{ textTransform: "lowercase" }}
                    />
                  </Box>
                </Box>

                {/* Pay Button for owner when they are also a customer participant with pending split evenly */}
                {shouldShowOwnerPayButton() && (
                  <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 3,
                    pt: 2,
                    borderTop: "1px solid #E0E0E0"
                  }}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => onPayAsCustomer(orderId)}
                      sx={{
                        textTransform: "lowercase",
                        minWidth: 120,
                      }}
                    >
                      pay your share
                    </Button>
                  </Box>
                )}

                {/* Refund Button - Conditional */}
                {shouldShowRefundButton() && (
                  <Box sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    mt: 3, 
                    pt: 2, 
                    borderTop: "1px solid #E0E0E0" 
                  }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleRefund}
                      sx={{ 
                        textTransform: "lowercase",
                        minWidth: 120,
                        backgroundColor: "#0085FF",
                        "&:hover": {
                          backgroundColor: "#0085FF",
                        }
                      }}
                    >
                      refund
                    </Button>
                  </Box>
                )}

                {/* Refund Details Button - Show when refunds exist */}
                {hasRefunds() && (
                  <Box sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    mt: 3, 
                    pt: 2, 
                    borderTop: "1px solid #E0E0E0" 
                  }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setRefundDetailsOpen(true)}
                      sx={{ 
                        textTransform: "lowercase",
                        minWidth: 120,
                        backgroundColor: "#0085FF",
                        "&:hover": {
                          backgroundColor: "#0085FF",
                        }
                      }}
                    >
                      refund details
                    </Button>
                  </Box>
                )}

              </Box>
            </ScrollableContainer>
          )}
        </ContentContainer>

        <ResizeHandle onMouseDown={handleResizeStart} />
      </CustomDialog>
    </CustomOverlay>

    {/* Business Cancel Detail Dialog - Business responds to customer cancellation requests */}
    <BusinessCancelSelectionDialog
      open={businessCancelDetailDialogOpen}
      onClose={() => setBusinessCancelDetailDialogOpen(false)}
      dialogData={businessCancelDetailDialogData}
      tempSelection={tempBusinessSelection}
      onCheckboxChange={setTempBusinessSelection}
      onSubmit={handleBusinessCancelSubmit}
    />

    {/* Business Cancel Dialog - New dialog for cancel item button */}
    <BusinessCancelDialog
      open={businessCancelDialogOpen}
      onClose={() => setBusinessCancelDialogOpen(false)}
      dialogData={businessCancelDialogData}
      onSubmit={handleNewBusinessCancelSubmit}
    />

    {/* Reject Confirmation Dialog */}
    <Dialog
      open={rejectConfirmOpen}
      onClose={handleRejectCancel}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 10003 }}
    >
      <DialogTitle sx={{ textTransform: "lowercase" }}>
        confirm rejection
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ textTransform: "lowercase", mb: 2 }}>
          are you sure you want to reject the customer cancellation request?
        </DialogContentText>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="enter reason for rejection (optional)"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          sx={{
            "& .MuiInputBase-input": {
              textTransform: "lowercase",
              "&::placeholder": {
                textTransform: "lowercase",
                opacity: 0.7,
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleRejectCancel}
          variant="contained"
          color="error"
          sx={{ textTransform: "lowercase" }}
        >
          no
        </Button>
        <Button 
          onClick={handleRejectConfirm}
          variant="contained"
          color="error"
          sx={{ textTransform: "lowercase" }}
        >
          yes, reject
        </Button>
      </DialogActions>
    </Dialog>

    {/* Business Refund Selection Dialog */}
    <BusinessRefundSelectionDialog
      open={businessRefundSelectionDialogOpen}
      onClose={() => setBusinessRefundSelectionDialogOpen(false)}
      dialogData={businessRefundSelectionDialogData}
      tempSelection={tempRefundSelection}
      onCheckboxChange={setTempRefundSelection}
      onSubmit={handleRefundSelectionSubmit}
    />

    {/* Business Refund Dialog */}
    <BusinessRefundDialog
      open={businessRefundDialogOpen}
      onClose={() => setBusinessRefundDialogOpen(false)}
      dialogData={businessRefundDialogData}
      onSubmit={handleRefundSubmit}
      totalRefundAmount={calculateRefundAmount(refundSelectedItems)}
      onSelectAllItems={handleSelectAllItemsForRefund}
      orderData={orderData}
    />

    {/* Business Refund Details Dialog */}
    <BusinessRefundDetailsDialog
      open={refundDetailsOpen}
      onClose={() => setRefundDetailsOpen(false)}
      orderData={orderData}
      onRefund={() => {
        // Re-activate refund flow from details dialog
        handleRefund();
      }}
    />

    {/* Options Menu */}
    <Menu
      anchorEl={optionsMenuAnchor}
      open={Boolean(optionsMenuAnchor)}
      onClose={handleOptionsMenuClose}
      PaperProps={{
        sx: {
          mt: 1,
          minWidth: 150,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          '& .MuiMenuItem-root': {
            textTransform: 'lowercase',
            fontSize: '0.875rem',
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: '#f0f4ff',
            },
          },
        },
      }}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
    >
      <MenuItem onClick={handleEditClick}>
        {editMode ? 'done editing' : 'edit'}
      </MenuItem>
    </Menu>
  </>
  );
};

export default BusinessOrderDetailsDialog;